// NovoKerja Admin Panel - Complete Dashboard with AI ID Verification
import React, { useState, useEffect } from 'react';
import OldJobsManagement from './components/OldJobsManagement';
import { collection, query, where, getDocs, limit, orderBy } from 'firebase/firestore';
import {
  auth,
  db,
  adminSignIn,
  adminSignOut,
  getPendingVerifications,
  approveVerification,
  rejectVerification,
  getActiveJobs,
  getPostedJobs,
  calculateCommission,
  getReleasedPayouts,
  getDisputedPayouts,
  getPendingPayouts,
  markPayoutAsPaid,
  getChoreReleasedPayouts,
  markChorePayoutAsPaid,
  releaseDisputedPayment,
  refundDisputedPayment,
  getDoc,
  doc,
  // Rule-based system functions (not hardcoded)
  getSystemHealth,
  getWorkflowExecutions,
  getAllPlatformConfig,
  getAuditLogs,
  // AI Dashboard functions
  getAIDecisions,
  getRuleEvolutions,
  approveRuleEvolution,
  rejectRuleEvolution,
  getAIPerformanceMetrics,
  // Community functions
  getCommunityPosts,
  updateCommunityPostStatus,
  replyToCommunityPost,
  resolveCommunityPost,
  addAIAnalysisToPost,
  // Messaging functions
  getAllUsers,
  searchUsers,
  sendAdminMessage,
  getAdminMessages,
  createBroadcastMessage,
  getBroadcastMessages,
  // 2FA functions
  setup2FA,
  verify2FACode,
  enable2FA,
  disable2FA,
  check2FAStatus,
  generateBackupCodes,
  // Billplz automated payout functions
  payWithBillplzRelay,
  getBankCodeFromName,
  BILLPLZ_BANK_CODES,
  // Hold payment functions
  holdPayment,
  releaseHold,
  getHeldPayouts
} from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  initSessionTimeout,
  checkRateLimit,
  recordLoginAttempt,
  logAdminAction,
  initializeAdminSession,
  destroyAdminSession,
  getClientIP
} from './security';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [sessionWarning, setSessionWarning] = useState(false);
  const [clientIP, setClientIP] = useState('unknown');

  // Data states
  const [verifications, setVerifications] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const [postedJobs, setPostedJobs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [disputedPayouts, setDisputedPayouts] = useState([]);
  const [heldPayouts, setHeldPayouts] = useState([]);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [processingId, setProcessingId] = useState(null);

  // Rule-based system states (not hardcoded)
  const [systemHealth, setSystemHealth] = useState(null);
  const [workflowExecutions, setWorkflowExecutions] = useState([]);
  const [platformConfig, setPlatformConfig] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // AI Dashboard states
  const [aiDecisions, setAiDecisions] = useState([]);
  const [ruleEvolutions, setRuleEvolutions] = useState([]);
  const [aiMetrics, setAiMetrics] = useState(null);

  // Community states
  const [communityPosts, setCommunityPosts] = useState([]);

  // Messaging states
  const [users, setUsers] = useState([]);
  const [adminMessages, setAdminMessages] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);

  // 2FA states
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [qrCode, setQRCode] = useState(null);
  const [twoFASecret, setTwoFASecret] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [pendingLogin, setPendingLogin] = useState(null);

  // Refresh states
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      if (user) {
        initializeAdminSession();
        loadAllData();

        // Log session start
        logAdminAction(db, 'SESSION_START', user.uid, {
          email: user.email,
          ip: clientIP
        });
      }
    });
    return unsubscribe;
  }, [clientIP]);

  useEffect(() => {
    // Get client IP on mount
    getClientIP().then(ip => setClientIP(ip));

    // Initialize session timeout if user is logged in
    if (user) {
      const cleanup = initSessionTimeout(
        () => {
          // Warning callback (2 minutes before timeout)
          setSessionWarning(true);
        },
        () => {
          // Timeout callback (force logout)
          handleSessionTimeout();
        }
      );

      return cleanup;
    }
  }, [user]);

  const loadAllData = async () => {
    setIsRefreshing(true);
    try {
      const [
        pendingIds,
        active,
        posted,
        releasedPayouts,
        disputed,
        pendingPayouts,
        held, // Payouts on hold
        chorePayouts, // Released chore escrows
        health,
        executions,
        config,
        logs,
        decisions,
        evolutions,
        metrics,
        community,
        allUsers,
        messages,
        broadcastList
      ] = await Promise.all([
        getPendingVerifications(),
        getActiveJobs(),
        getPostedJobs(),
        getReleasedPayouts(),
        getDisputedPayouts(),
        getPendingPayouts(),
        getHeldPayouts(), // Payouts on hold
        getChoreReleasedPayouts(), // Phase 3D — released chore escrows surface here
        getSystemHealth(),
        getWorkflowExecutions(100), // Increased from 50
        getAllPlatformConfig(),
        getAuditLogs(200), // Increased from 100
        getAIDecisions(100), // Increased from 50
        getRuleEvolutions(50), // Increased from 20
        getAIPerformanceMetrics(),
        getCommunityPosts(100),
        getAllUsers(200), // Increased from 100
        getAdminMessages(null, 100),
        getBroadcastMessages(50)
      ]);
      setVerifications(pendingIds);
      setActiveJobs(active);
      setPostedJobs(posted);
      // Combine escrow payouts and deduplicate by ID (exclude held ones)
      const seen = new Set();
      const allPayouts = [...releasedPayouts, ...pendingPayouts, ...(chorePayouts || [])].filter(p => {
        if (seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      });
      const nonHeldPayouts = allPayouts.filter(p => !p.onHold);
      setPayouts(nonHeldPayouts);
      setDisputedPayouts(disputed);
      setHeldPayouts(held);
      setSystemHealth(health);
      setWorkflowExecutions(executions);
      setPlatformConfig(config);
      setAuditLogs(logs);
      setAiDecisions(decisions);
      setRuleEvolutions(evolutions);
      setAiMetrics(metrics);
      setCommunityPosts(community);
      setUsers(allUsers);
      setAdminMessages(messages);
      setBroadcasts(broadcastList);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    // Check rate limiting
    const rateCheck = checkRateLimit(clientIP);
    if (!rateCheck.allowed) {
      setLoginError(rateCheck.reason);
      await logAdminAction(db, 'LOGIN_RATE_LIMITED', 'system', {
        ip: clientIP,
        email,
        reason: rateCheck.reason
      });
      return;
    }

    try {
      const userCredential = await adminSignIn(email, password);
      const userId = userCredential.user.uid;

      // Check if 2FA is enabled for this user
      const twoFA = await check2FAStatus(userId);

      if (twoFA?.enabled) {
        // 2FA is enabled - store pending login and show 2FA prompt
        setPendingLogin({ email, userId, twoFASecret: twoFA.twoFactorSecret });
        setTwoFARequired(true);

        // Immediately sign out until 2FA is verified
        await adminSignOut();
        setUser(null);

        await logAdminAction(db, 'LOGIN_2FA_REQUIRED', userId, {
          ip: clientIP,
          email
        });
      } else {
        // No 2FA - login successful
        recordLoginAttempt(clientIP, true);
        await logAdminAction(db, 'LOGIN_SUCCESS', email, {
          ip: clientIP,
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      setLoginError(error.message);

      // Record failed login
      recordLoginAttempt(clientIP, false);
      await logAdminAction(db, 'LOGIN_FAILED', 'system', {
        ip: clientIP,
        email,
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  const handle2FAVerify = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      setLoginError('Please enter a 6-digit code');
      return;
    }

    if (!pendingLogin) {
      setLoginError('No pending login found');
      return;
    }

    setLoginError('');

    try {
      // Verify the TOTP code
      const isValid = verify2FACode(twoFACode, pendingLogin.twoFASecret);

      if (!isValid) {
        throw new Error('Invalid 2FA code. Please try again.');
      }

      // Code is valid - complete the login
      await adminSignIn(email, password);

      // Record successful login
      recordLoginAttempt(clientIP, true);
      await logAdminAction(db, 'LOGIN_2FA_SUCCESS', pendingLogin.userId, {
        ip: clientIP,
        email: pendingLogin.email,
        timestamp: new Date().toISOString()
      });

      // Clear 2FA state
      setTwoFARequired(false);
      setTwoFACode('');
      setPendingLogin(null);
    } catch (error) {
      setLoginError(error.message);
      await logAdminAction(db, 'LOGIN_2FA_FAILED', pendingLogin.userId, {
        ip: clientIP,
        email: pendingLogin.email,
        error: error.message
      });
    }
  };

  const handleLogout = async () => {
    if (user) {
      await logAdminAction(db, 'LOGOUT', user.uid, {
        ip: clientIP,
        timestamp: new Date().toISOString()
      });
    }

    destroyAdminSession();
    adminSignOut();
    setUser(null);
    setSessionWarning(false);
  };

  const handleSessionTimeout = async () => {
    if (user) {
      await logAdminAction(db, 'SESSION_TIMEOUT', user.uid, {
        ip: clientIP,
        reason: 'Inactivity timeout (15 minutes)'
      });
    }

    alert('Your session has expired due to inactivity. Please log in again.');
    handleLogout();
  };

  const extendSession = () => {
    setSessionWarning(false);
    // Session will auto-extend on user activity
  };

  const handleApprove = async (verificationId) => {
    if (!window.confirm('Approve this ID verification?')) return;

    setProcessingId(verificationId);
    try {
      await approveVerification(verificationId, user.uid, adminNotes);
      alert('ID Verification Approved! ✅');
      setSelectedVerification(null);
      setAdminNotes('');
      await loadAllData();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (verificationId, reason) => {
    if (!reason) {
      alert('Please select a rejection reason');
      return;
    }

    if (!window.confirm('Reject this ID verification?')) return;

    setProcessingId(verificationId);
    try {
      await rejectVerification(verificationId, user.uid, reason, adminNotes);
      alert('ID Verification Rejected');
      setSelectedVerification(null);
      setAdminNotes('');
      await loadAllData();
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessingId(null);
    }
  };

  // Login Screen
  if (loading) {
    return <div style={styles.loading}>Loading...</div>;
  }

  if (!user) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginBox}>
          <h1 style={styles.loginTitle}>🎯 NovoKerja Admin</h1>
          <p style={styles.loginSubtitle}>Platform Management Dashboard</p>

          {twoFARequired ? (
            // 2FA Verification Screen
            <div style={styles.loginForm}>
              <div style={{
                background: '#EFF6FF',
                padding: '16px',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <h3 style={{ margin: '0 0 8px 0', color: '#1E40AF' }}>🔐 Two-Factor Authentication</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#3B82F6' }}>
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <input
                type="text"
                placeholder="000000"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength="6"
                style={{
                  ...styles.input,
                  fontSize: '24px',
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  marginBottom: '16px'
                }}
                autoFocus
              />

              {loginError && <p style={styles.error}>{loginError}</p>}

              <button
                onClick={handle2FAVerify}
                disabled={twoFACode.length !== 6}
                style={{
                  ...styles.loginButton,
                  opacity: twoFACode.length !== 6 ? 0.6 : 1,
                  cursor: twoFACode.length !== 6 ? 'not-allowed' : 'pointer',
                  marginBottom: '12px'
                }}
              >
                Verify Code
              </button>

              <button
                onClick={() => {
                  setTwoFARequired(false);
                  setTwoFACode('');
                  setPendingLogin(null);
                  setLoginError('');
                }}
                style={{
                  ...styles.loginButton,
                  background: '#6B7280',
                  fontSize: '14px',
                  padding: '10px'
                }}
              >
                ← Back to Login
              </button>
            </div>
          ) : (
            // Regular Login Form
            <form onSubmit={handleLogin} style={styles.loginForm}>
              <input
                type="email"
                placeholder="Admin Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={styles.input}
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.input}
                required
              />
              {loginError && <p style={styles.error}>{loginError}</p>}
              <button type="submit" style={styles.loginButton}>
                Sign In
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div style={styles.dashboard}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.headerTitle}>NovoKerja Admin</h1>
          <span style={styles.headerSubtitle}>{user.email}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {lastRefreshed && (
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
              Last updated: {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={loadAllData}
            disabled={isRefreshing}
            style={{
              ...styles.logoutButton,
              background: isRefreshing ? '#9CA3AF' : '#3B82F6',
              cursor: isRefreshing ? 'not-allowed' : 'pointer'
            }}
          >
            {isRefreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
          </button>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      </header>

      {/* Main Layout with Sidebar and Content */}
      <div style={styles.mainLayout}>
        {/* Left Sidebar - Consolidated 5 Tabs */}
        <div style={styles.sidebar}>
          {/* Main Navigation */}
          <div style={{ marginBottom: '8px', padding: '8px', background: '#F3F4F6', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '8px', fontWeight: '600' }}>MAIN</div>
            <button
              onClick={() => setActiveTab('dashboard')}
              style={activeTab === 'dashboard' ? styles.tabActive : styles.tab}
            >
              📊 Dashboard
            </button>
          </div>

          {/* Operations - Daily Work */}
          <div style={{ marginBottom: '8px', padding: '8px', background: '#FEF3C7', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#92400E', marginBottom: '8px', fontWeight: '600' }}>OPERATIONS</div>
            <button
              onClick={() => setActiveTab('id-verification')}
              style={activeTab === 'id-verification' ? styles.tabActive : styles.tab}
            >
              🪪 Verifications {verifications.filter(v => v.status === 'pending' || v.status === 'manual_review').length > 0 && (
                <span style={{ background: '#EF4444', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', marginLeft: '8px' }}>
                  {verifications.filter(v => v.status === 'pending' || v.status === 'manual_review').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('payouts')}
              style={activeTab === 'payouts' ? styles.tabActive : styles.tab}
            >
              💸 Payouts {payouts.length > 0 && (
                <span style={{ background: '#F59E0B', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', marginLeft: '8px' }}>
                  {payouts.length}
                </span>
              )}
              {disputedPayouts.length > 0 && (
                <span title="Disputes need admin review" style={{ background: '#DC2626', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', marginLeft: '6px' }}>
                  🚨 {disputedPayouts.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('commission')}
              style={activeTab === 'commission' ? styles.tabActive : styles.tab}
            >
              💰 Revenue
            </button>
          </div>

          {/* Jobs */}
          <div style={{ marginBottom: '8px', padding: '8px', background: '#DBEAFE', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#1E40AF', marginBottom: '8px', fontWeight: '600' }}>JOBS</div>
            <button
              onClick={() => setActiveTab('active-jobs')}
              style={activeTab === 'active-jobs' ? styles.tabActive : styles.tab}
            >
              ✅ Active ({activeJobs.length})
            </button>
            <button
              onClick={() => setActiveTab('posted-jobs')}
              style={activeTab === 'posted-jobs' ? styles.tabActive : styles.tab}
            >
              📋 Posted ({postedJobs.length})
            </button>
            <button
              onClick={() => setActiveTab('old-jobs')}
              style={activeTab === 'old-jobs' ? styles.tabActive : styles.tab}
            >
              🗂️ Old Jobs
            </button>
          </div>

          {/* Communications */}
          <div style={{ marginBottom: '8px', padding: '8px', background: '#D1FAE5', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#065F46', marginBottom: '8px', fontWeight: '600' }}>COMMS</div>
            <button
              onClick={() => setActiveTab('community')}
              style={activeTab === 'community' ? styles.tabActive : styles.tab}
            >
              💬 Community {communityPosts.filter(p => p.status === 'pending').length > 0 && (
                <span style={{ background: '#10B981', color: 'white', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', marginLeft: '8px' }}>
                  {communityPosts.filter(p => p.status === 'pending').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              style={activeTab === 'messages' ? styles.tabActive : styles.tab}
            >
              📨 Messages
            </button>
          </div>

          {/* System Config */}
          <div style={{ padding: '8px', background: '#F3F4F6', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '8px', fontWeight: '600' }}>SYSTEM</div>
            <button
              onClick={() => setActiveTab('job-progress')}
              style={activeTab === 'job-progress' ? styles.tabActive : styles.tab}
            >
              📊 Job Progress
            </button>
            <button
              onClick={() => setActiveTab('rules')}
              style={activeTab === 'rules' ? styles.tabActive : styles.tab}
            >
              📜 Rules & Config
            </button>
            <button
              onClick={() => setActiveTab('ai-dashboard')}
              style={activeTab === 'ai-dashboard' ? styles.tabActive : styles.tab}
            >
              🤖 AI Monitor
            </button>
            <button
              onClick={() => setActiveTab('audit-logs')}
              style={activeTab === 'audit-logs' ? styles.tabActive : styles.tab}
            >
              📋 Audit Logs
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              style={activeTab === 'settings' ? styles.tabActive : styles.tab}
            >
              ⚙️ Settings
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div style={styles.content}>
        {activeTab === 'dashboard' && (
          <DashboardTab health={systemHealth} executions={workflowExecutions} />
        )}

        {activeTab === 'job-progress' && (
          <JobProgressionTab />
        )}

        {activeTab === 'notifications' && (
          <NotificationsTab platformConfig={platformConfig} />
        )}

        {activeTab === 'banners' && (
          <BannersTab platformConfig={platformConfig} />
        )}

        {activeTab === 'status-rules' && (
          <StatusRulesTab platformConfig={platformConfig} />
        )}

        {activeTab === 'rules' && (
          <RulesConfigTab platformConfig={platformConfig} />
        )}

        {activeTab === 'id-verification' && (
          <IDVerificationTab
            verifications={verifications}
            selected={selectedVerification}
            onSelect={setSelectedVerification}
            onApprove={handleApprove}
            onReject={handleReject}
            notes={adminNotes}
            onNotesChange={setAdminNotes}
            processing={processingId}
          />
        )}

        {activeTab === 'active-jobs' && (
          <ActiveJobsTab jobs={activeJobs} />
        )}

        {activeTab === 'posted-jobs' && (
          <PostedJobsTab jobs={postedJobs} />
        )}

        {activeTab === 'old-jobs' && (
          <OldJobsManagement />
        )}

        {activeTab === 'payouts' && (
          <PayoutsTab
            payouts={payouts}
            disputedPayouts={disputedPayouts}
            heldPayouts={heldPayouts}
            onMarkAsPaid={async (escrowId, payoutDetails) => {
              // Dispatch by source — chore escrows live in choreEscrows
              const payout = payouts.find(p => p.id === escrowId);
              if (payout?.source === 'chore') {
                await markChorePayoutAsPaid(escrowId, user.uid, payoutDetails);
              } else {
                await markPayoutAsPaid(escrowId, user.uid, payoutDetails);
              }
              await loadAllData(); // Reload data after marking as paid
            }}
            onReleaseDisputed={async (escrowId, notes) => {
              await releaseDisputedPayment(escrowId, user.uid, notes);
              await loadAllData();  // Reload data after resolving dispute
            }}
            onRefundDisputed={async (escrowId, notes) => {
              await refundDisputedPayment(escrowId, user.uid, notes);
              await loadAllData(); // Reload data after resolving dispute
            }}
            onPayWithBillplz={async (escrowId, payoutDetails) => {
              // Auto Billplz payout for chores not wired yet — payWithBillplzRelay
              // reads escrowPayments only. Tell admin to use Manual Pay instead.
              const payout = payouts.find(p => p.id === escrowId);
              if (payout?.source === 'chore') {
                throw new Error('Auto Billplz payout for chores not wired yet (Phase 4). Use Manual Pay — does the bank transfer manually then marks the chore escrow PAID.');
              }
              const result = await payWithBillplzRelay(escrowId, payoutDetails);
              await loadAllData(); // Reload data after Billplz payout
              return result;
            }}
            onHoldPayment={async (escrowId, reason) => {
              await holdPayment(escrowId, user.uid, reason);
              await loadAllData(); // Reload data after holding payment
            }}
            onReleaseHold={async (escrowId) => {
              await releaseHold(escrowId, user.uid);
              await loadAllData(); // Reload data after releasing hold
            }}
          />
        )}

        {activeTab === 'commission' && (
          <CommissionTab jobs={activeJobs} />
        )}

        {activeTab === 'ai-dashboard' && (
          <AIDashboardTab
            decisions={aiDecisions}
            evolutions={ruleEvolutions}
            metrics={aiMetrics}
            onApprove={async (evolutionId, notes) => {
              await approveRuleEvolution(evolutionId, user.uid, notes);
              await loadAllData();
            }}
            onReject={async (evolutionId, reason, notes) => {
              await rejectRuleEvolution(evolutionId, user.uid, reason, notes);
              await loadAllData();
            }}
          />
        )}

        {activeTab === 'audit-logs' && (
          <AuditLogsTab logs={auditLogs} />
        )}

        {activeTab === 'community' && (
          <CommunityTab
            posts={communityPosts}
            onReply={async (postId, replyText) => {
              await replyToCommunityPost(postId, user.uid, replyText);
              await loadAllData();
            }}
            onResolve={async (postId, notes) => {
              await resolveCommunityPost(postId, user.uid, notes);
              await loadAllData();
            }}
            onUpdateStatus={async (postId, status, notes) => {
              await updateCommunityPostStatus(postId, user.uid, status, notes);
              await loadAllData();
            }}
            onAIAnalyze={async (postId, analysis) => {
              await addAIAnalysisToPost(postId, analysis);
              await loadAllData();
            }}
          />
        )}

        {activeTab === 'messages' && (
          <MessagesTab
            users={users}
            messages={adminMessages}
            broadcasts={broadcasts}
            currentUserId={user.uid}
            onSendMessage={async (recipientId, title, message, priority, category) => {
              await sendAdminMessage(user.uid, recipientId, title, message, priority, category);
              await loadAllData();
            }}
            onSendBroadcast={async (title, message, priority, category, targetAudience) => {
              await createBroadcastMessage(user.uid, title, message, priority, category, targetAudience);
              await loadAllData();
            }}
            onRefresh={() => loadAllData()}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            user={user}
            twoFAStatus={twoFAStatus}
            setTwoFAStatus={setTwoFAStatus}
            qrCode={qrCode}
            setQRCode={setQRCode}
            twoFASecret={twoFASecret}
            setTwoFASecret={setTwoFASecret}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            backupCodes={backupCodes}
            setBackupCodes={setBackupCodes}
          />
        )}
        </div>
      </div>
    </div>
  );
}

// ID Verification Tab Component
function IDVerificationTab({ verifications, selected, onSelect, onApprove, onReject, notes, onNotesChange, processing }) {
  const [rejectionReason, setRejectionReason] = useState('');

  if (verifications.length === 0) {
    return (
      <div style={styles.emptyState}>
        <h2>✅ All Caught Up!</h2>
        <p>No pending ID verifications at the moment.</p>
      </div>
    );
  }

  if (selected) {
    const data = selected.extractedData || {};
    const validation = selected.validationResult || {};

    return (
      <div style={styles.reviewModal}>
        <div style={styles.modalHeader}>
          <h2>Review ID Verification</h2>
          <button onClick={() => onSelect(null)} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.modalContent}>
          {/* ID Image */}
          <div style={styles.section}>
            <h3>ID Document Photo</h3>
            {selected.imageUrl ? (
              <img src={selected.imageUrl} alt="ID" style={styles.idImage} />
            ) : (
              <div style={styles.noImage}>No image available</div>
            )}
          </div>

          {/* AI Extracted Data */}
          <div style={styles.section}>
            <h3>🤖 AI-Extracted Information</h3>
            <table style={styles.dataTable}>
              <tbody>
                <tr>
                  <td style={styles.dataLabel}>Full Name:</td>
                  <td style={styles.dataValue}>{data.fullName || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>IC / Passport Number:</td>
                  <td style={styles.dataValue}>{data.icNumber || data.passportNumber || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Date of Birth:</td>
                  <td style={styles.dataValue}>{data.dateOfBirth || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Gender:</td>
                  <td style={styles.dataValue}>{data.gender || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Address:</td>
                  <td style={styles.dataValue}>{data.address || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Nationality:</td>
                  <td style={styles.dataValue}>{data.nationality || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* AI Confidence Scores */}
          <div style={styles.section}>
            <h3>📊 Verification Scores</h3>
            <div style={styles.scores}>
              <div style={styles.scoreCard}>
                <div style={styles.scoreLabel}>AI Confidence</div>
                <div style={{
                  ...styles.scoreValue,
                  color: (data.confidence || 0) >= 0.8 ? '#10B981' : '#F59E0B'
                }}>
                  {((data.confidence || 0) * 100).toFixed(0)}%
                </div>
              </div>
              <div style={styles.scoreCard}>
                <div style={styles.scoreLabel}>Profile Match</div>
                <div style={{
                  ...styles.scoreValue,
                  color: (validation.matchScore || 0) >= 80 ? '#10B981' : '#EF4444'
                }}>
                  {validation.matchScore || 0}%
                </div>
              </div>
            </div>
          </div>

          {/* Issues */}
          {validation.issues && validation.issues.length > 0 && (
            <div style={styles.section}>
              <h3>⚠️ Issues Detected</h3>
              <ul style={styles.issuesList}>
                {validation.issues.map((issue, i) => (
                  <li key={i} style={styles.issueItem}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Rejection Reason */}
          <div style={styles.section}>
            <h3>Rejection Reason (if rejecting)</h3>
            <select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              style={styles.select}
            >
              <option value="">Select reason...</option>
              <option>Image too blurry or low quality</option>
              <option>ID document has expired</option>
              <option>Name does not match profile</option>
              <option>Date of birth does not match profile</option>
              <option>Invalid ID format or unreadable</option>
              <option>Photo does not match profile picture</option>
              <option>Age below 18 years (Malaysian labor law)</option>
              <option>Document appears to be tampered or fake</option>
            </select>
          </div>

          {/* Admin Notes */}
          <div style={styles.section}>
            <h3>Admin Notes (Optional)</h3>
            <textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Add notes about this verification..."
              style={styles.textarea}
            />
          </div>

          {/* Actions */}
          <div style={styles.modalActions}>
            <button
              onClick={() => onReject(selected.id, rejectionReason)}
              disabled={processing === selected.id}
              style={styles.rejectButton}
            >
              ❌ Reject
            </button>
            <button
              onClick={() => onApprove(selected.id)}
              disabled={processing === selected.id}
              style={styles.approveButton}
            >
              ✅ Approve
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getStatusStyle = (status) => {
    const styles = {
      auto_approved: { bg: '#D1FAE5', color: '#065F46', text: '✅ Auto-Approved' },
      admin_approved: { bg: '#D1FAE5', color: '#065F46', text: '✅ Admin Approved' },
      auto_rejected: { bg: '#FEE2E2', color: '#991B1B', text: '❌ Auto-Rejected' },
      admin_rejected: { bg: '#FEE2E2', color: '#991B1B', text: '❌ Admin Rejected' },
      manual_review: { bg: '#FEF3C7', color: '#92400E', text: '🔍 Needs Review' },
      processing: { bg: '#E0E7FF', color: '#3730A3', text: '⏳ Processing' }
    };
    return styles[status] || { bg: '#F3F4F6', color: '#6B7280', text: status };
  };

  return (
    <div style={styles.verificationList}>
      <h2>All ID Verifications ({verifications.length})</h2>
      {verifications.map(v => {
        const statusStyle = getStatusStyle(v.status);
        return (
          <div key={v.id} style={styles.verificationCard} onClick={() => onSelect(v)}>
            <div style={styles.cardHeader}>
              <div>
                <h3>{v.extractedData?.fullName || 'Unknown'}</h3>
                <p style={styles.cardSubtitle}>
                  {v.idType === 'mykad' ? 'MyKad' : 'Passport'} •
                  {v.extractedData?.icNumber || v.extractedData?.passportNumber || 'No ID'}
                </p>
              </div>
              <button style={styles.reviewButton}>View →</button>
            </div>
            <div style={styles.cardScores}>
              <span style={{
                ...styles.badge,
                background: statusStyle.bg,
                color: statusStyle.color,
                fontWeight: '600'
              }}>
                {statusStyle.text}
              </span>
              <span style={styles.badge}>
                AI: {((v.extractedData?.confidence || 0) * 100).toFixed(0)}%
              </span>
              {v.validationResult?.matchScore !== undefined && (
                <span style={styles.badge}>
                  Match: {v.validationResult.matchScore}%
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Active Jobs Tab with Unified Workflow View
function ActiveJobsTab({ jobs }) {
  const [workflowExecutions, setWorkflowExecutions] = React.useState({});
  const [expandedJobs, setExpandedJobs] = React.useState({});
  const [loadingWorkflows, setLoadingWorkflows] = React.useState(true);

  // Fetch workflow executions for all jobs
  React.useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        setLoadingWorkflows(true);
        const executionsMap = {};

        for (const job of jobs) {
          // Try to find workflow execution by requestId (entityId)
          const requestId = job.requestId || job.staffingRequestId;
          if (requestId) {
            const q = query(
              collection(db, 'workflowExecutions'),
              where('entityId', '==', requestId),
              limit(1)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
              const execution = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
              executionsMap[job.id] = execution;
            }
          }
        }

        setWorkflowExecutions(executionsMap);
        setLoadingWorkflows(false);
      } catch (error) {
        console.error('Error fetching workflows:', error);
        setLoadingWorkflows(false);
      }
    };

    if (jobs.length > 0) {
      fetchWorkflows();
    }
  }, [jobs]);

  const toggleJobExpansion = (jobId) => {
    setExpandedJobs(prev => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  const getWorkflowProgress = (execution) => {
    if (!execution) return { step: 0, total: 11, stepName: 'N/A', status: 'unknown' };

    const stepOrder = [
      'job_posted', 'find_workers', 'notify_workers', 'worker_applied',
      'worker_approved', 'payment_secured', 'assignment_created',
      'work_completed', 'business_confirms', 'payment_released', 'payment_received'
    ];

    const currentStepIndex = stepOrder.indexOf(execution.currentStep);
    return {
      step: currentStepIndex + 1,
      total: 11,
      stepName: execution.currentStep?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      status: execution.status
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: '#10B981',
      in_progress: '#3B82F6',
      running: '#3B82F6',
      waiting: '#F59E0B',
      failed: '#EF4444',
      unknown: '#6B7280'
    };
    return colors[status] || colors.unknown;
  };

  return (
    <div style={styles.jobsList}>
      <h2>Active Jobs with Workflow Progress ({jobs.length})</h2>

      {loadingWorkflows && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280' }}>
          Loading workflow data...
        </div>
      )}

      {jobs.map(job => {
        const execution = workflowExecutions[job.id];
        const progress = getWorkflowProgress(execution);
        const isExpanded = expandedJobs[job.id];

        return (
          <div key={job.id} style={{
            ...styles.jobCard,
            border: execution ? `3px solid ${getStatusColor(progress.status)}` : styles.jobCard.border
          }}>
            {/* Job Header with Workflow Progress */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 8px 0' }}>{job.jobTitle}</h3>

                {/* Workflow Progress Bar */}
                {execution && (
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                      <span style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: getStatusColor(progress.status)
                      }}>
                        🔄 Step {progress.step}/{progress.total}: {progress.stepName}
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        background: progress.status === 'completed' ? '#D1FAE5' :
                                   progress.status === 'in_progress' || progress.status === 'running' ? '#DBEAFE' :
                                   progress.status === 'waiting' ? '#FEF3C7' : '#FEE2E2',
                        color: progress.status === 'completed' ? '#065F46' :
                               progress.status === 'in_progress' || progress.status === 'running' ? '#1E40AF' :
                               progress.status === 'waiting' ? '#92400E' : '#991B1B'
                      }}>
                        {progress.status}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div style={{
                      width: '100%',
                      height: '8px',
                      background: '#E5E7EB',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${(progress.step / progress.total) * 100}%`,
                        height: '100%',
                        background: getStatusColor(progress.status),
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Expand/Collapse Button */}
              {execution && (
                <button
                  onClick={() => toggleJobExpansion(job.id)}
                  style={{
                    padding: '8px 16px',
                    background: isExpanded ? '#3B82F6' : '#E5E7EB',
                    color: isExpanded ? '#FFFFFF' : '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  {isExpanded ? '▼ Hide Workflow' : '▶ View Workflow'}
                </button>
              )}
            </div>

            {/* Job Details */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
              <p style={{ margin: '4px 0' }}><strong>Worker:</strong> {job.workerName}</p>
              <p style={{ margin: '4px 0' }}><strong>Business:</strong> {job.businessName}</p>
              <p style={{ margin: '4px 0' }}><strong>Rate:</strong> RM {job.hourlyRate}/{job.paymentType === 'per_job' ? 'job' : job.paymentType === 'per_day' ? 'day' : 'hr'}{job.paymentType !== 'per_job' ? ` × ${job.duration}${job.paymentType === 'per_day' ? 'd' : 'h'}` : ''}</p>
              <p style={{ margin: '4px 0' }}><strong>Total:</strong> RM {job.totalPay}</p>
              <p style={{ margin: '4px 0' }}><strong>Status:</strong> <span style={{
                color: job.status === 'completed' ? '#10B981' : '#3B82F6',
                fontWeight: '600'
              }}>{job.status}</span></p>
              {!execution && (
                <p style={{ margin: '4px 0', color: '#F59E0B' }}>
                  <strong>⚠️ No workflow found</strong>
                </p>
              )}
            </div>

            {/* Expanded Workflow Diagram */}
            {isExpanded && execution && (
              <div style={{
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: '2px solid #E5E7EB'
              }}>
                <WorkflowVisualDiagram
                  execution={execution}
                  logs={[]}
                  aiDecisions={[]}
                  db={db}
                />
              </div>
            )}
          </div>
        );
      })}

      {jobs.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
          <h3>No Active Jobs</h3>
          <p>Active job assignments will appear here</p>
        </div>
      )}
    </div>
  );
}

// Posted Jobs Tab
function PostedJobsTab({ jobs }) {
  return (
    <div style={styles.jobsList}>
      <h2>All Posted Jobs ({jobs.length})</h2>
      {jobs.map(job => (
        <div key={job.id} style={styles.jobCard}>
          <h3>{job.jobTitle}</h3>
          <p><strong>Business:</strong> {job.businessName || 'N/A'}</p>
          <p><strong>Rate:</strong> RM {job.hourlyRate}/{job.paymentType === 'per_job' ? 'job' : job.paymentType === 'per_day' ? 'day' : 'hr'}</p>
          <p><strong>Workers Needed:</strong> {job.workersNeeded}</p>
          <p><strong>Applications:</strong> {job.pendingWorkers?.length || 0}</p>
        </div>
      ))}
    </div>
  );
}

// Payouts Tab
function PayoutsTab({ payouts, disputedPayouts, heldPayouts = [], onMarkAsPaid, onReleaseDisputed, onRefundDisputed, onPayWithBillplz, onHoldPayment, onReleaseHold }) {
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [billplzProcessingId, setBillplzProcessingId] = useState(null);
  const [workerBankAccounts, setWorkerBankAccounts] = useState({});
  const [activeFilter, setActiveFilter] = useState('pending'); // NEW: Filter state
  const [holdReason, setHoldReason] = useState(''); // NEW: Hold reason
  const [showHoldModal, setShowHoldModal] = useState(null); // NEW: Hold modal state
  const [showConfirmModal, setShowConfirmModal] = useState(null); // NEW: Confirmation modal
  const [relayStatus, setRelayStatus] = useState('checking'); // 'online' | 'offline' | 'checking'

  // Check if payout relay is running on localhost
  useEffect(() => {
    const checkRelay = async () => {
      try {
        const resp = await fetch('http://localhost:3737/health', { signal: AbortSignal.timeout(2000) });
        if (resp.ok) {
          setRelayStatus('online');
        } else {
          setRelayStatus('offline');
        }
      } catch {
        setRelayStatus('offline');
      }
    };
    checkRelay();
    const interval = setInterval(checkRelay, 15000); // Re-check every 15s
    return () => clearInterval(interval);
  }, []);

  // Filter out any invalid payouts and normalize field names (handle trailing spaces in test data)
  const validPayouts = payouts.filter(p => {
    if (!p || typeof p !== 'object') {
      console.warn('Invalid payout object:', p);
      return false;
    }
    return true;
  }).map(p => {
    // Normalize field names - handle trailing spaces in field names from bad test data
    const amount = p.amount || p['amount '] || 0;
    // Calculate workerPayout if not stored (for old records)
    const isPromo = p.isPromo || false;
    const platformFeePercent = p.platformFeePercent ?? (isPromo ? 0 : 15);
    const platformFee = p.platformFee ?? (amount * platformFeePercent / 100);
    const workerPayout = p.workerPayout ?? (amount - platformFee);

    return {
      id: p.id,
      amount: amount,                            // Original job amount
      isPromo: isPromo,                          // Promo flag
      platformFeePercent: platformFeePercent,    // Fee percentage (0 for promo, 15 for regular)
      platformFee: platformFee,                  // Platform fee amount
      workerPayout: workerPayout,                // What worker actually receives
      workerName: p.workerName || p['workerName '] || 'Unknown',
      workerId: p.workerId || p['workerId '] || '',
      jobTitle: p.jobTitle || p['jobTitle '] || 'N/A',
      businessName: p.businessName || p['businessName '] || 'N/A',
      assignmentId: p.assignmentId || p['assignmentId '] || 'N/A',
      releasedAt: p.releasedAt || p['releasedAt '] || null,
      createdAt: p.createdAt || null,
      hourlyRate: p.hourlyRate || p['hourlyRate '] || 0,
      hoursWorked: p.hoursWorked || p['hoursWorked '] || 0,
      duration: p.duration || 0,
      paymentType: p.paymentType || 'per_hour',
      status: p.status,
      onHold: p.onHold || false,
      holdReason: p.holdReason || ''
    };
  });

  // Normalize held payouts
  const validHeldPayouts = (heldPayouts || []).map(p => {
    const amount = p.amount || 0;
    const isPromo = p.isPromo || false;
    const platformFeePercent = p.platformFeePercent ?? (isPromo ? 0 : 15);
    const platformFee = p.platformFee ?? (amount * platformFeePercent / 100);
    const workerPayout = p.workerPayout ?? (amount - platformFee);

    return {
      id: p.id,
      amount: amount,
      isPromo: isPromo,
      platformFeePercent: platformFeePercent,
      platformFee: platformFee,
      workerPayout: workerPayout,
      workerName: p.workerName || 'Unknown',
      workerId: p.workerId || '',
      jobTitle: p.jobTitle || 'N/A',
      businessName: p.businessName || 'N/A',
      assignmentId: p.assignmentId || 'N/A',
      releasedAt: p.releasedAt || null,
      createdAt: p.createdAt || null,
      holdReason: p.holdReason || '',
      holdAt: p.holdAt || null
    };
  });

  // Use workerPayout for totals (what we actually need to pay out)
  const totalPending = validPayouts.reduce((sum, p) => sum + (parseFloat(p.workerPayout) || 0), 0);
  const totalHeld = validHeldPayouts.reduce((sum, p) => sum + (parseFloat(p.workerPayout || p.amount) || 0), 0);
  const totalDisputed = (disputedPayouts || []).reduce((sum, p) => sum + (parseFloat(p.workerPayout || p.amount) || 0), 0);
  // Calculate total platform fees collected
  const totalPlatformFees = validPayouts.reduce((sum, p) => sum + (parseFloat(p.platformFee) || 0), 0);

  // NEW: Calculate urgency based on days pending
  const getUrgency = (payout) => {
    const createdTime = payout.releasedAt?.seconds || payout.createdAt?.seconds || Date.now() / 1000;
    const daysPending = (Date.now() / 1000 - createdTime) / 86400; // seconds to days

    if (daysPending < 2) return { level: 'NORMAL', color: '#10B981', label: 'Normal' };
    if (daysPending < 5) return { level: 'MEDIUM', color: '#F59E0B', label: 'Medium' };
    if (daysPending < 7) return { level: 'HIGH', color: '#F97316', label: 'High' };
    return { level: 'CRITICAL', color: '#EF4444', label: 'CRITICAL' };
  };

  // Handle Billplz automated payout
  const handleBillplzPay = async (payout) => {
    const bankDetails = workerBankAccounts[payout.workerId];

    if (!bankDetails) {
      alert('❌ Worker bank details not found. Please ask worker to update their bank information.');
      return;
    }

    if (!bankDetails.accountNumber || !bankDetails.bankName) {
      alert('❌ Incomplete bank details. Worker needs to provide bank name and account number.');
      return;
    }

    // Get bank code from bank name
    const bankCode = getBankCodeFromName(bankDetails.bankName);
    if (!bankCode) {
      alert(`❌ Bank "${bankDetails.bankName}" not supported for automated payout. Please use manual bank transfer.`);
      return;
    }

    // Use workerPayout (after platform fee deduction) for the actual transfer
    const workerPayout = parseFloat(payout.workerPayout) || parseFloat(payout.amount) || 0;
    if (workerPayout <= 0) {
      alert('❌ Invalid payout amount.');
      return;
    }

    const confirmMsg = `🏦 Auto Pay via Billplz\n\n` +
      `Worker: ${payout.workerName}\n` +
      `Job Amount: RM ${(parseFloat(payout.amount) || 0).toFixed(2)}\n` +
      `Platform Fee (${payout.platformFeePercent || 0}%): -RM ${(parseFloat(payout.platformFee) || 0).toFixed(2)}\n` +
      `Worker Payout: RM ${workerPayout.toFixed(2)}\n` +
      `Bank: ${bankDetails.bankName}\n` +
      `Account: ${bankDetails.accountNumber}\n` +
      `Account Name: ${bankDetails.accountName || 'N/A'}\n\n` +
      `Proceed with automated bank transfer?`;

    if (!window.confirm(confirmMsg)) return;

    setBillplzProcessingId(payout.id);

    try {
      // Pass workerPayout as the amount to transfer (after fee deduction)
      const result = await onPayWithBillplz(payout.id, {
        bankCode,
        bankAccountNumber: bankDetails.accountNumber,
        recipientName: bankDetails.accountName || payout.workerName,
        amount: workerPayout, // Use workerPayout, not original amount
        workerId: payout.workerId,
        jobTitle: payout.jobTitle
      });

      if (result.success) {
        alert(`✅ Billplz Payout Initiated!\n\nPayment Order ID: ${result.id}\nStatus: ${result.status}\n\nFunds will be transferred to worker's bank account.`);
      } else {
        alert(`❌ Billplz Payout Failed!\n\n${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Billplz payout error:', error);
      alert(`❌ Billplz Payout Error!\n\n${error.message}`);
    } finally {
      setBillplzProcessingId(null);
    }
  };

  // Load bank account info for each worker (UPDATED: uses users/ collection with bankDetails field)
  useEffect(() => {
    const loadBankAccounts = async () => {
      const accounts = {};
      for (const payout of validPayouts) {
        if (payout.workerId && !workerBankAccounts[payout.workerId]) {
          try {
            // Load from users/ collection with bankDetails field
            const userDoc = await getDoc(doc(db, 'users', payout.workerId));
            if (userDoc.exists()) {
              accounts[payout.workerId] = userDoc.data().bankDetails || null;
            }
          } catch (error) {
            console.error(`Error loading bank account for ${payout.workerId}:`, error);
          }
        }
      }
      setWorkerBankAccounts(accounts);
    };

    if (validPayouts.length > 0) {
      loadBankAccounts();
    }
  }, [payouts]); // Use payouts instead of validPayouts to avoid infinite loop

  const exportToCSV = () => {
    if (validPayouts.length === 0) {
      alert('No payouts to export');
      return;
    }

    // CSV headers
    const headers = [
      'Worker Name',
      'Amount (RM)',
      'Bank Name',
      'Account Number',
      'Account Holder Name',
      'IC Number',
      'Job Title',
      'Business Name',
      'Assignment ID',
      'Released Date'
    ];

    // CSV rows
    const rows = validPayouts.map(payout => {
      const bankDetails = workerBankAccounts[payout.workerId];
      const releasedDate = payout.releasedAt?.seconds
        ? new Date(payout.releasedAt.seconds * 1000).toLocaleDateString('en-MY')
        : payout.createdAt?.seconds
        ? new Date(payout.createdAt.seconds * 1000).toLocaleDateString('en-MY')
        : 'N/A';

      return [
        payout.workerName || 'N/A',
        (parseFloat(payout.amount) || 0).toFixed(2),
        bankDetails?.bankName || 'NOT PROVIDED',
        bankDetails?.accountNumber || 'NOT PROVIDED',
        bankDetails?.accountName || 'NOT PROVIDED', // UPDATED: accountName instead of accountHolderName
        bankDetails?.icNumber || 'N/A',
        payout.jobTitle || 'N/A',
        payout.businessName || 'N/A',
        payout.assignmentId || payout.id || 'N/A',
        releasedDate
      ].map(field => `"${field}"`).join(',');
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `payouts_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleMarkAsPaid = async () => {
    if (!paymentRef) {
      alert('Please enter payment reference number');
      return;
    }

    if (!window.confirm(`Confirm manual payout of RM${(parseFloat(selectedPayout.amount) || 0).toFixed(2)} to ${selectedPayout.workerName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      await onMarkAsPaid(selectedPayout.id, {
        method: paymentMethod,
        reference: paymentRef,
        notes: paymentNotes
      });
      alert(`✅ Payout Recorded! RM${(parseFloat(selectedPayout.amount) || 0).toFixed(2)} marked as paid to ${selectedPayout.workerName}`);
      setSelectedPayout(null);
      setPaymentRef('');
      setPaymentNotes('');
      setPaymentMethod('bank_transfer');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Add handlers for dispute resolution
  const handleReleaseDisputed = async () => {
    if (!window.confirm(`Approve worker and release payment of RM${(parseFloat(selectedDispute.amount) || 0).toFixed(2)} to ${selectedDispute.workerName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      await onReleaseDisputed(selectedDispute.id, resolutionNotes);
      alert(`✅ Dispute Resolved! Payment released to worker: ${selectedDispute.workerName}`);
      setSelectedDispute(null);
      setResolutionNotes('');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRefundDisputed = async () => {
    if (!window.confirm(`Refund payment of RM${(parseFloat(selectedDispute.amount) || 0).toFixed(2)} to business: ${selectedDispute.businessName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      await onRefundDisputed(selectedDispute.id, resolutionNotes);
      alert(`✅ Dispute Resolved! Payment refunded to business: ${selectedDispute.businessName}`);
      setSelectedDispute(null);
      setResolutionNotes('');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // NEW: Handle putting payment on hold
  const handleHoldPayment = async () => {
    if (!showHoldModal) return;

    if (!holdReason.trim()) {
      alert('Please provide a reason for holding this payment');
      return;
    }

    setProcessing(true);
    try {
      await onHoldPayment(showHoldModal.id, holdReason);
      alert(`✅ Payment held! RM${(parseFloat(showHoldModal.amount) || 0).toFixed(2)} for ${showHoldModal.workerName} is now on hold.`);
      setShowHoldModal(null);
      setHoldReason('');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // NEW: Handle releasing hold
  const handleReleaseHold = async (payout) => {
    if (!window.confirm(`Release hold on RM${(parseFloat(payout.amount) || 0).toFixed(2)} for ${payout.workerName}?\n\nThis will move the payment back to pending payouts.`)) {
      return;
    }

    setProcessing(true);
    try {
      await onReleaseHold(payout.id);
      alert(`✅ Hold released! Payment is now back in pending payouts.`);
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // NEW: Confirmation before Billplz pay
  const handleConfirmedBillplzPay = async () => {
    if (!showConfirmModal) return;
    setShowConfirmModal(null);
    await handleBillplzPay(showConfirmModal);
  };

  if (validPayouts.length === 0 && (!disputedPayouts || disputedPayouts.length === 0) && validHeldPayouts.length === 0) {
    return (
      <div style={styles.emptyState}>
        <h2>✅ All Caught Up!</h2>
        <p>No pending payouts or disputed payments at the moment.</p>
      </div>
    );
  }

  if (selectedPayout) {
    const bankAccount = workerBankAccounts[selectedPayout.workerId];

    return (
      <div style={styles.reviewModal}>
        <div style={styles.modalHeader}>
          <h2>Record Manual Payout</h2>
          <button onClick={() => setSelectedPayout(null)} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.modalContent}>
          {/* Payout Summary */}
          <div style={styles.section}>
            <h3>Payment Details</h3>
            <table style={styles.dataTable}>
              <tbody>
                <tr>
                  <td style={styles.dataLabel}>Worker Name:</td>
                  <td style={styles.dataValue}>{selectedPayout.workerName}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Job Title:</td>
                  <td style={styles.dataValue}>{selectedPayout.jobTitle}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Business:</td>
                  <td style={styles.dataValue}>{selectedPayout.businessName}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Amount to Pay:</td>
                  <td style={styles.dataValue}>
                    <strong style={{ color: '#10B981', fontSize: '18px' }}>
                      RM {(parseFloat(selectedPayout.amount) || 0).toFixed(2)}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Released On:</td>
                  <td style={styles.dataValue}>{formatDate(selectedPayout.releasedAt)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Worker Bank Account Info */}
          <div style={styles.section}>
            <h3>Worker Bank Account</h3>
            {bankAccount ? (
              <table style={styles.dataTable}>
                <tbody>
                  <tr>
                    <td style={styles.dataLabel}>Bank Name:</td>
                    <td style={styles.dataValue}>{bankAccount.bankName}</td>
                  </tr>
                  <tr>
                    <td style={styles.dataLabel}>Account Number:</td>
                    <td style={styles.dataValue}>
                      <strong style={{ fontSize: '16px' }}>{bankAccount.accountNumber}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td style={styles.dataLabel}>Account Holder:</td>
                    <td style={styles.dataValue}>{bankAccount.accountName || bankAccount.accountHolderName}</td>
                  </tr>
                  <tr>
                    <td style={styles.dataLabel}>IC Number:</td>
                    <td style={styles.dataValue}>{bankAccount.icNumber}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div style={{
                padding: '16px',
                background: '#FEF2F2',
                borderRadius: '8px',
                borderLeft: '4px solid #EF4444',
                color: '#991B1B'
              }}>
                ⚠️ Worker has not provided bank account details yet. Please contact worker to add bank information in their profile.
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div style={styles.section}>
            <h3>Payment Method</h3>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              style={styles.select}
            >
              <option value="bank_transfer">Bank Transfer (Online Banking)</option>
              <option value="maybank2u">Maybank2u</option>
              <option value="cimb_clicks">CIMB Clicks</option>
              <option value="public_bank">Public Bank Online</option>
              <option value="rhb_now">RHB Now</option>
              <option value="billplz">Billplz (Future)</option>
              <option value="manual">Manual (Cash/Cheque)</option>
            </select>
          </div>

          {/* Payment Reference */}
          <div style={styles.section}>
            <h3>Payment Reference Number *</h3>
            <input
              type="text"
              value={paymentRef}
              onChange={(e) => setPaymentRef(e.target.value)}
              placeholder="e.g., TRX20251011123456 or receipt number"
              style={styles.input}
              required
            />
            <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
              Enter your bank transaction reference or receipt number
            </p>
          </div>

          {/* Notes */}
          <div style={styles.section}>
            <h3>Admin Notes (Optional)</h3>
            <textarea
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              placeholder="Add any notes about this payout (e.g., partial payment, adjustment reason, etc.)"
              style={styles.textarea}
            />
          </div>

          {/* Actions */}
          <div style={styles.modalActions}>
            <button
              onClick={() => setSelectedPayout(null)}
              style={styles.rejectButton}
            >
              Cancel
            </button>
            <button
              onClick={handleMarkAsPaid}
              disabled={processing || !paymentRef}
              style={{
                ...styles.approveButton,
                opacity: (!paymentRef || processing) ? 0.5 : 1,
                cursor: (!paymentRef || processing) ? 'not-allowed' : 'pointer'
              }}
            >
              {processing ? 'Processing...' : `✅ Confirm RM${(parseFloat(selectedPayout.amount) || 0).toFixed(2)} Paid`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Dispute Resolution Modal
  if (selectedDispute) {
    return (
      <div style={styles.reviewModal}>
        <div style={styles.modalHeader}>
          <h2>Resolve Payment Dispute</h2>
          <button onClick={() => setSelectedDispute(null)} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.modalContent}>
          {/* Dispute Details */}
          <div style={styles.section}>
            <h3>💼 Job & Payment Details</h3>
            <table style={styles.dataTable}>
              <tbody>
                <tr>
                  <td style={styles.dataLabel}>Worker Name:</td>
                  <td style={styles.dataValue}>{selectedDispute.workerName || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Business Name:</td>
                  <td style={styles.dataValue}>{selectedDispute.businessName || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Job Title:</td>
                  <td style={styles.dataValue}>{selectedDispute.jobTitle || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Assignment ID:</td>
                  <td style={styles.dataValue}>{selectedDispute.assignmentId || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Disputed Amount:</td>
                  <td style={styles.dataValue}>
                    <strong style={{ color: '#F59E0B', fontSize: '18px' }}>
                      RM {(parseFloat(selectedDispute.amount) || 0).toFixed(2)}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Disputed At:</td>
                  <td style={styles.dataValue}>{formatDate(selectedDispute.disputedAt)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Dispute Reason */}
          <div style={styles.section}>
            <h3>⚠️ Dispute Reason</h3>
            <div style={{
              padding: '16px',
              background: '#FEF3C7',
              borderRadius: '8px',
              borderLeft: '4px solid #F59E0B',
              fontSize: '15px',
              lineHeight: '1.6'
            }}>
              <strong>Raised by:</strong> {selectedDispute.disputeRaisedBy === 'business' ? selectedDispute.businessName : selectedDispute.workerName}
              <br /><br />
              <strong>Reason:</strong> {selectedDispute.disputeReason || 'No reason provided'}
              {selectedDispute.disputeNotes && (
                <>
                  <br /><br />
                  <strong>Additional notes:</strong> {selectedDispute.disputeNotes}
                </>
              )}
            </div>
          </div>

          {/* Admin Resolution Notes */}
          <div style={styles.section}>
            <h3>Admin Resolution Notes</h3>
            <textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Add notes about your decision (e.g., reviewed evidence, contacted parties, etc.)"
              style={styles.textarea}
            />
          </div>

          {/* Resolution Options */}
          <div style={{
            padding: '16px',
            background: '#F9FAFB',
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <h4 style={{ marginBottom: '12px', color: '#6B7280' }}>Resolution Options:</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#6B7280' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Release to Worker:</strong> Approve worker, payment goes to worker (business loses dispute)
              </li>
              <li>
                <strong>Refund to Business:</strong> Approve business, payment refunded to business (worker loses dispute)
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div style={styles.modalActions}>
            <button
              onClick={handleRefundDisputed}
              disabled={processing}
              style={{
                ...styles.rejectButton,
                opacity: processing ? 0.5 : 1,
                cursor: processing ? 'not-allowed' : 'pointer'
              }}
            >
              {processing ? 'Processing...' : '💰 Refund to Business'}
            </button>
            <button
              onClick={handleReleaseDisputed}
              disabled={processing}
              style={{
                ...styles.approveButton,
                opacity: processing ? 0.5 : 1,
                cursor: processing ? 'not-allowed' : 'pointer'
              }}
            >
              {processing ? 'Processing...' : '✅ Release to Worker'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Filter tabs configuration
  const filterTabs = [
    { key: 'all', label: 'All', count: validPayouts.length + validHeldPayouts.length + (disputedPayouts?.length || 0) },
    { key: 'pending', label: 'Pending', count: validPayouts.length, color: '#10B981' },
    { key: 'onhold', label: 'On Hold', count: validHeldPayouts.length, color: '#F59E0B' },
    { key: 'disputed', label: 'Disputed', count: disputedPayouts?.length || 0, color: '#EF4444' }
  ];

  // Hold Modal
  if (showHoldModal) {
    return (
      <div style={styles.reviewModal}>
        <div style={styles.modalHeader}>
          <h2>⏸️ Put Payment On Hold</h2>
          <button onClick={() => { setShowHoldModal(null); setHoldReason(''); }} style={styles.closeButton}>✕</button>
        </div>
        <div style={styles.modalContent}>
          <div style={styles.section}>
            <h3>Payment Details</h3>
            <table style={styles.dataTable}>
              <tbody>
                <tr><td style={styles.dataLabel}>Worker:</td><td style={styles.dataValue}>{showHoldModal.workerName}</td></tr>
                <tr><td style={styles.dataLabel}>Job:</td><td style={styles.dataValue}>{showHoldModal.jobTitle}</td></tr>
                <tr><td style={styles.dataLabel}>Amount:</td><td style={styles.dataValue}><strong style={{ color: '#10B981' }}>RM {(parseFloat(showHoldModal.amount) || 0).toFixed(2)}</strong></td></tr>
              </tbody>
            </table>
          </div>
          <div style={styles.section}>
            <h3>Reason for Hold *</h3>
            <select value={holdReason} onChange={(e) => setHoldReason(e.target.value)} style={styles.select}>
              <option value="">-- Select a reason --</option>
              <option value="Pending investigation">Pending investigation</option>
              <option value="Worker complaint received">Worker complaint received</option>
              <option value="Business complaint received">Business complaint received</option>
              <option value="Suspicious activity">Suspicious activity</option>
              <option value="Incomplete work verification">Incomplete work verification</option>
              <option value="Bank details need verification">Bank details need verification</option>
              <option value="Other">Other (add notes below)</option>
            </select>
            <textarea
              value={holdReason === 'Other' ? '' : undefined}
              onChange={(e) => setHoldReason(e.target.value)}
              placeholder="Add additional notes about why this payment is being held..."
              style={{ ...styles.textarea, marginTop: '12px' }}
            />
          </div>
          <div style={styles.modalActions}>
            <button onClick={() => { setShowHoldModal(null); setHoldReason(''); }} style={styles.rejectButton}>Cancel</button>
            <button onClick={handleHoldPayment} disabled={processing || !holdReason.trim()} style={{ ...styles.approveButton, background: '#F59E0B', opacity: (!holdReason.trim() || processing) ? 0.5 : 1 }}>
              {processing ? 'Processing...' : '⏸️ Put On Hold'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation Modal for Auto Pay
  if (showConfirmModal) {
    const bankDetails = workerBankAccounts[showConfirmModal.workerId];
    const modalWorkerPayout = showConfirmModal.workerPayout || showConfirmModal.amount;
    const modalPlatformFee = showConfirmModal.platformFee || 0;
    return (
      <div style={styles.reviewModal}>
        <div style={styles.modalHeader}>
          <h2>🏦 Confirm Auto Pay</h2>
          <button onClick={() => setShowConfirmModal(null)} style={styles.closeButton}>✕</button>
        </div>
        <div style={styles.modalContent}>
          <div style={{ padding: '16px', background: '#F0FDF4', borderRadius: '8px', borderLeft: '4px solid #10B981', marginBottom: '24px' }}>
            <strong>⚠️ Please verify all details before proceeding</strong>
            <p style={{ margin: '8px 0 0 0', color: '#6B7280' }}>This will initiate an immediate bank transfer to the worker.</p>
          </div>
          <div style={styles.section}>
            <h3>Payment Details</h3>
            <table style={styles.dataTable}>
              <tbody>
                <tr><td style={styles.dataLabel}>Worker:</td><td style={styles.dataValue}><strong>{showConfirmModal.workerName}</strong></td></tr>
                <tr><td style={styles.dataLabel}>Job:</td><td style={styles.dataValue}>{showConfirmModal.jobTitle}</td></tr>
                <tr><td style={styles.dataLabel}>Business:</td><td style={styles.dataValue}>{showConfirmModal.businessName}</td></tr>
                {showConfirmModal.isPromo && (
                  <tr><td style={styles.dataLabel}>Type:</td><td style={styles.dataValue}><span style={{ background: '#8B5CF6', color: 'white', padding: '2px 8px', borderRadius: '4px' }}>PROMO (No Fee)</span></td></tr>
                )}
              </tbody>
            </table>

            {/* Fee Breakdown */}
            <div style={{ marginTop: '16px', padding: '16px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280' }}>Job Amount:</span>
                <span>RM {(parseFloat(showConfirmModal.amount) || 0).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#6B7280' }}>Platform Fee ({showConfirmModal.platformFeePercent || 0}%):</span>
                <span style={{ color: modalPlatformFee > 0 ? '#EF4444' : '#10B981' }}>
                  {modalPlatformFee > 0 ? `-RM ${modalPlatformFee.toFixed(2)}` : 'Waived'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '2px solid #E5E7EB', marginTop: '8px' }}>
                <span style={{ fontWeight: '600', color: '#374151' }}>Worker Receives:</span>
                <span style={{ fontWeight: 'bold', fontSize: '20px', color: '#10B981' }}>RM {(parseFloat(modalWorkerPayout) || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
          {bankDetails && (
            <div style={styles.section}>
              <h3>Bank Account</h3>
              <table style={styles.dataTable}>
                <tbody>
                  <tr><td style={styles.dataLabel}>Bank:</td><td style={styles.dataValue}>{bankDetails.bankName}</td></tr>
                  <tr><td style={styles.dataLabel}>Account:</td><td style={styles.dataValue}><strong>{bankDetails.accountNumber}</strong></td></tr>
                  <tr><td style={styles.dataLabel}>Name:</td><td style={styles.dataValue}>{bankDetails.accountName || 'N/A'}</td></tr>
                </tbody>
              </table>
            </div>
          )}
          <div style={styles.modalActions}>
            <button onClick={() => setShowConfirmModal(null)} style={styles.rejectButton}>Cancel</button>
            <button onClick={() => setShowHoldModal(showConfirmModal)} style={{ ...styles.rejectButton, background: '#F59E0B', color: 'white' }}>⏸️ Hold Instead</button>
            <button onClick={handleConfirmedBillplzPay} style={styles.approveButton}>✅ Confirm & Pay</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.payoutsList}>
      {/* Relay status banner */}
      {relayStatus === 'offline' && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px', padding: '10px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>⚠️</span>
          <span style={{ color: '#92400E', fontSize: '13px' }}>
            <strong>Payout Relay offline.</strong> Start it with: <code style={{ background: '#FDE68A', padding: '2px 6px', borderRadius: '4px' }}>node scripts/payout-relay.js</code>
          </span>
        </div>
      )}
      {relayStatus === 'online' && (
        <div style={{ background: '#F0FDF4', border: '1px solid #10B981', borderRadius: '8px', padding: '8px 16px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981', display: 'inline-block' }}></span>
          <span style={{ color: '#065F46', fontSize: '13px' }}>Payout Relay connected (localhost:3737)</span>
        </div>
      )}
      {/* Header with totals */}
      <div style={styles.payoutsHeader}>
        <h2>Worker Payouts</h2>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button onClick={exportToCSV} style={styles.exportButton} disabled={validPayouts.length === 0}>
            📊 Export to CSV
          </button>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ ...styles.totalPending, background: '#F0FDF4' }}>
              <div style={{ fontSize: '12px', color: '#059669' }}>Pending Payout</div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#059669' }}>RM {totalPending.toFixed(2)}</div>
            </div>
            {totalPlatformFees > 0 && (
              <div style={{ ...styles.totalPending, background: '#EDE9FE' }}>
                <div style={{ fontSize: '12px', color: '#7C3AED' }}>Platform Fees</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#7C3AED' }}>RM {totalPlatformFees.toFixed(2)}</div>
              </div>
            )}
            {totalHeld > 0 && (
              <div style={{ ...styles.totalPending, background: '#FEF3C7' }}>
                <div style={{ fontSize: '12px', color: '#D97706' }}>On Hold</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#D97706' }}>RM {totalHeld.toFixed(2)}</div>
              </div>
            )}
            {totalDisputed > 0 && (
              <div style={{ ...styles.totalPending, background: '#FEE2E2' }}>
                <div style={{ fontSize: '12px', color: '#DC2626' }}>Disputed</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#DC2626' }}>RM {totalDisputed.toFixed(2)}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: activeFilter === tab.key ? '2px solid #3B82F6' : '2px solid #E5E7EB',
              background: activeFilter === tab.key ? '#EFF6FF' : 'white',
              color: activeFilter === tab.key ? '#1D4ED8' : '#6B7280',
              fontWeight: activeFilter === tab.key ? '600' : '400',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {tab.label}
            <span style={{
              background: tab.color || '#6B7280',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Pending Payouts Section */}
      {(activeFilter === 'all' || activeFilter === 'pending') && validPayouts.length > 0 && (
        <>
          {activeFilter === 'all' && <h3 style={{ marginTop: '24px', marginBottom: '16px', color: '#059669' }}>💰 Pending Payouts ({validPayouts.length})</h3>}
          <div style={{ marginTop: '16px' }}>
            {validPayouts.map(payout => {
              const urgency = getUrgency(payout);
              return (
                <div key={payout.id} style={styles.payoutCard}>
                  <div style={styles.payoutCardHeader}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <h3 style={{ margin: 0 }}>{payout.workerName || 'Unknown Worker'}</h3>
                        <span style={{ backgroundColor: urgency.color, color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                          {urgency.label}
                        </span>
                        {payout.isPromo && (
                          <span style={{ backgroundColor: '#8B5CF6', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                            PROMO
                          </span>
                        )}
                      </div>
                      <p style={styles.cardSubtitle}>{payout.jobTitle || 'N/A'} • {payout.businessName || 'N/A'}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10B981' }}>RM {(parseFloat(payout.workerPayout) || 0).toFixed(2)}</div>
                      {payout.platformFee > 0 && (
                        <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                          <span style={{ textDecoration: 'line-through' }}>RM {(parseFloat(payout.amount) || 0).toFixed(2)}</span>
                          <span style={{ color: '#EF4444', marginLeft: '4px' }}>-{payout.platformFeePercent}%</span>
                        </div>
                      )}
                      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>Released: {formatDate(payout.releasedAt || payout.createdAt)}</div>
                    </div>
                  </div>

                  {/* Fee Breakdown */}
                  <div style={{ padding: '12px', background: '#F3F4F6', borderRadius: '8px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: '#6B7280' }}>Job Amount:</span>
                      <span>RM {(parseFloat(payout.amount) || 0).toFixed(2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: '#6B7280' }}>Platform Fee ({payout.platformFeePercent}%):</span>
                      <span style={{ color: payout.platformFee > 0 ? '#EF4444' : '#10B981' }}>
                        {payout.platformFee > 0 ? `-RM ${payout.platformFee.toFixed(2)}` : 'Waived (Promo)'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', paddingTop: '8px', borderTop: '1px solid #E5E7EB' }}>
                      <span style={{ color: '#374151' }}>Worker Payout:</span>
                      <span style={{ color: '#10B981' }}>RM {(parseFloat(payout.workerPayout) || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  <div style={styles.payoutCardDetails}>
                    <div style={styles.payoutDetail}><span style={styles.payoutDetailLabel}>Rate:</span><span>RM {(parseFloat(payout.hourlyRate) || 0).toFixed(2)}/{payout.paymentType === 'per_job' ? 'job' : payout.paymentType === 'per_day' ? 'day' : 'hr'}</span></div>
                    <div style={styles.payoutDetail}><span style={styles.payoutDetailLabel}>{payout.paymentType === 'per_job' ? 'Duration:' : payout.paymentType === 'per_day' ? 'Days:' : 'Hours:'}</span><span>{payout.hoursWorked || payout.duration || 0}{payout.paymentType === 'per_day' ? 'd' : 'h'}</span></div>
                    <div style={styles.payoutDetail}><span style={styles.payoutDetailLabel}>Assignment:</span><span>{payout.assignmentId || 'N/A'}</span></div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setShowConfirmModal(payout)}
                      disabled={billplzProcessingId === payout.id || !workerBankAccounts[payout.workerId]}
                      style={{
                        ...styles.markPaidButton,
                        background: billplzProcessingId === payout.id ? '#9CA3AF' : !workerBankAccounts[payout.workerId] ? '#D1D5DB' : '#2563EB',
                        flex: 2,
                        cursor: (billplzProcessingId === payout.id || !workerBankAccounts[payout.workerId]) ? 'not-allowed' : 'pointer'
                      }}
                      title={!workerBankAccounts[payout.workerId] ? 'Worker bank details not available' : 'Auto pay via Billplz'}
                    >
                      {billplzProcessingId === payout.id ? '⏳ Processing...' : '🏦 Auto Pay (Billplz)'}
                    </button>
                    <button onClick={() => setSelectedPayout(payout)} style={{ ...styles.markPaidButton, flex: 2 }}>💸 Manual Pay</button>
                    <button
                      onClick={() => setShowHoldModal(payout)}
                      style={{ ...styles.markPaidButton, flex: 1, background: '#F59E0B' }}
                      title="Put this payment on hold for review"
                    >
                      ⏸️ Hold
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Held Payouts Section */}
      {(activeFilter === 'all' || activeFilter === 'onhold') && validHeldPayouts.length > 0 && (
        <>
          {activeFilter === 'all' && <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#D97706' }}>⏸️ On Hold ({validHeldPayouts.length})</h3>}
          <div style={{ marginTop: '16px' }}>
            {validHeldPayouts.map(payout => (
              <div key={payout.id} style={{ ...styles.payoutCard, border: '2px solid #FEF3C7', background: '#FFFBEB' }}>
                <div style={styles.payoutCardHeader}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{ margin: 0 }}>{payout.workerName || 'Unknown Worker'}</h3>
                      <span style={{ backgroundColor: '#F59E0B', color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>ON HOLD</span>
                    </div>
                    <p style={styles.cardSubtitle}>{payout.jobTitle || 'N/A'} • {payout.businessName || 'N/A'}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#D97706' }}>RM {(parseFloat(payout.amount) || 0).toFixed(2)}</div>
                    <div style={{ fontSize: '12px', color: '#92400E', marginTop: '4px' }}>Held: {formatDate(payout.holdAt)}</div>
                  </div>
                </div>

                {payout.holdReason && (
                  <div style={{ padding: '12px', background: 'white', borderRadius: '8px', borderLeft: '4px solid #F59E0B', marginBottom: '12px' }}>
                    <strong style={{ fontSize: '13px', color: '#92400E' }}>Hold Reason:</strong>
                    <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>{payout.holdReason}</p>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button onClick={() => handleReleaseHold(payout)} disabled={processing} style={{ ...styles.markPaidButton, flex: 1, background: '#10B981' }}>
                    ▶️ Release Hold & Pay
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Disputed Payments Section */}
      {(activeFilter === 'all' || activeFilter === 'disputed') && disputedPayouts && disputedPayouts.length > 0 && (
        <>
          {activeFilter === 'all' && <h3 style={{ marginTop: '32px', marginBottom: '16px', color: '#DC2626' }}>⚠️ Disputed - Admin Review Required ({disputedPayouts.length})</h3>}
          <div style={{ marginTop: '16px' }}>
            {disputedPayouts.map(dispute => (
              <div key={dispute.id} style={{ ...styles.payoutCard, border: '2px solid #FECACA', background: '#FEF2F2' }}>
                <div style={styles.payoutCardHeader}>
                  <div>
                    <h3>{dispute.workerName || 'Unknown Worker'}</h3>
                    <p style={styles.cardSubtitle}>{dispute.jobTitle || 'N/A'} • {dispute.businessName || 'N/A'}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#DC2626' }}>RM {(parseFloat(dispute.amount) || 0).toFixed(2)}</div>
                    <div style={{ fontSize: '12px', color: '#991B1B', marginTop: '4px', fontWeight: '600' }}>⚠️ DISPUTED</div>
                  </div>
                </div>

                <div style={{ ...styles.payoutCardDetails, background: '#FECACA', marginBottom: '12px' }}>
                  <div style={styles.payoutDetail}><span style={styles.payoutDetailLabel}>Disputed At:</span><span>{formatDate(dispute.disputedAt)}</span></div>
                  <div style={styles.payoutDetail}><span style={styles.payoutDetailLabel}>Raised By:</span><span>{dispute.disputeRaisedBy === 'business' ? 'Business' : 'Worker'}</span></div>
                  <div style={styles.payoutDetail}><span style={styles.payoutDetailLabel}>Assignment:</span><span>{dispute.assignmentId || 'N/A'}</span></div>
                </div>

                <div style={{ padding: '12px', background: 'white', borderRadius: '8px', borderLeft: '4px solid #DC2626', marginBottom: '12px' }}>
                  <strong style={{ fontSize: '13px', color: '#991B1B' }}>Dispute Reason:</strong>
                  <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px', marginBottom: 0 }}>
                    {dispute.disputeReason ? (dispute.disputeReason.length > 100 ? dispute.disputeReason.substring(0, 100) + '...' : dispute.disputeReason) : 'No reason provided'}
                  </p>
                </div>

                <button onClick={() => setSelectedDispute(dispute)} style={{ ...styles.markPaidButton, background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)' }}>
                  ⚖️ Review & Resolve Dispute
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Empty state for specific filters */}
      {activeFilter === 'pending' && validPayouts.length === 0 && (
        <div style={styles.emptyState}><h3>✅ No pending payouts</h3><p>All payments have been processed or are on hold.</p></div>
      )}
      {activeFilter === 'onhold' && validHeldPayouts.length === 0 && (
        <div style={styles.emptyState}><h3>✅ No payments on hold</h3><p>No payments are currently being held for review.</p></div>
      )}
      {activeFilter === 'disputed' && (!disputedPayouts || disputedPayouts.length === 0) && (
        <div style={styles.emptyState}><h3>✅ No disputes</h3><p>There are no payment disputes to review.</p></div>
      )}
    </div>
  );
}

// Commission Tab
function CommissionTab({ jobs }) {
  const totalCommission = calculateCommission(jobs);
  const completedJobs = jobs.filter(j => j.status === 'completed' || j.status === 'payment_released');
  const completedCommission = calculateCommission(completedJobs);

  return (
    <div style={styles.commissionContainer}>
      <h2>Platform Commission (10% Fee)</h2>
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Active Jobs</div>
          <div style={styles.statValue}>{jobs.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Completed Jobs</div>
          <div style={styles.statValue}>{completedJobs.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Potential Commission</div>
          <div style={styles.statValue}>RM {totalCommission.toFixed(2)}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Earned Commission</div>
          <div style={styles.statValue}>RM {completedCommission.toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
}

// Dashboard Tab (System Health Metrics - Not Hardcoded)
function DashboardTab({ health, executions }) {
  if (!health) {
    return <div style={styles.emptyState}><h2>Loading...</h2></div>;
  }

  const systemStatus = health.workflowSuccessRate >= 90 ? '🟢 All Systems OK' :
                       health.workflowSuccessRate >= 70 ? '🟡 Minor Issues' :
                       '🔴 Critical Issues';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2>System Health Dashboard</h2>
        <div style={{ fontSize: '18px', fontWeight: '600' }}>{systemStatus}</div>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Workflow Success Rate (24h)</div>
          <div style={{ ...styles.statValue, color: health.workflowSuccessRate >= 90 ? '#10B981' : '#EF4444' }}>
            {health.workflowSuccessRate}%
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active Executions</div>
          <div style={styles.statValue}>{health.activeExecutions}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Jobs</div>
          <div style={styles.statValue}>{health.totalJobs}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Completed Workflows</div>
          <div style={styles.statValue}>{health.completedWorkflows}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Avg Payment Release Time</div>
          <div style={styles.statValue}>{health.avgPaymentReleaseTime}h</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Last 24h Executions</div>
          <div style={styles.statValue}>{health.last24hExecutions}</div>
        </div>
      </div>

      <h3 style={{ marginTop: '32px', marginBottom: '16px' }}>Recent Workflow Executions</h3>
      <div>
        {executions.slice(0, 5).map(exec => (
          <div key={exec.id} style={styles.jobCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h4>{exec.workflowName || exec.workflowId || 'Unknown Workflow'}</h4>
                <p style={styles.cardSubtitle}>
                  {exec.currentStep || 'N/A'} • {exec.status || 'unknown'}
                </p>
              </div>
              <div style={{
                padding: '6px 12px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '600',
                background: exec.status === 'completed' ? '#D1FAE5' :
                           exec.status === 'failed' ? '#FEE2E2' :
                           exec.status === 'in_progress' ? '#DBEAFE' : '#F3F4F6',
                color: exec.status === 'completed' ? '#065F46' :
                       exec.status === 'failed' ? '#991B1B' :
                       exec.status === 'in_progress' ? '#1E40AF' : '#6B7280'
              }}>
                {exec.status || 'unknown'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Visual Workflow Diagram Component (n8n-style) - FIXED to match actual workflow
function WorkflowVisualDiagram({ execution, logs, aiDecisions, db }) {
  const [selectedStep, setSelectedStep] = React.useState(null);
  const [stepContextData, setStepContextData] = React.useState({});

  // Fetch contextual data for a step using Firebase v9+ modular API
  const fetchStepContextData = async (stepId) => {
    if (!execution.entityId || !db) return null;

    try {
      const entityId = execution.entityId;
      let contextData = {};

      // For all job-related steps, fetch the job details
      if (['job_posted', 'find_workers', 'notify_workers', 'worker_applied', 'worker_approved'].includes(stepId)) {
        const jobDocRef = doc(db, 'staffingRequests', entityId);
        const jobDoc = await getDoc(jobDocRef);
        if (jobDoc.exists()) {
          contextData.job = { id: jobDoc.id, ...jobDoc.data() };
        }
      }

      // For worker-related steps, fetch worker details
      if (['worker_applied', 'worker_approved'].includes(stepId) && execution.metadata?.workerId) {
        const workerDocRef = doc(db, 'users', execution.metadata.workerId);
        const workerDoc = await getDoc(workerDocRef);
        if (workerDoc.exists()) {
          contextData.worker = { id: workerDoc.id, ...workerDoc.data() };
        }
      }

      // For payment steps, fetch escrow/payment details
      if (['payment_secured', 'payment_released', 'payment_received'].includes(stepId)) {
        const escrowQuery = query(
          collection(db, 'escrowPayments'),
          where('staffingRequestId', '==', entityId),
          limit(1)
        );
        const escrowSnapshot = await getDocs(escrowQuery);
        if (!escrowSnapshot.empty) {
          contextData.payment = { id: escrowSnapshot.docs[0].id, ...escrowSnapshot.docs[0].data() };
        }
      }

      // For assignment steps, fetch assignment details
      if (['assignment_created', 'work_completed', 'business_confirms'].includes(stepId)) {
        const assignmentQuery = query(
          collection(db, 'activeAssignments'),
          where('requestId', '==', entityId),
          limit(1)
        );
        const assignmentSnapshot = await getDocs(assignmentQuery);
        if (!assignmentSnapshot.empty) {
          contextData.assignment = { id: assignmentSnapshot.docs[0].id, ...assignmentSnapshot.docs[0].data() };
        }
      }

      return contextData;
    } catch (error) {
      console.error('Error fetching step context data:', error);
      return null;
    }
  };

  // Handle step selection
  const handleStepClick = async (stepId) => {
    const newSelectedStep = selectedStep === stepId ? null : stepId;
    setSelectedStep(newSelectedStep);

    // Fetch context data if selecting a new step and don't have it yet
    if (newSelectedStep && !stepContextData[stepId]) {
      const contextData = await fetchStepContextData(stepId);
      if (contextData) {
        // Sanitize all timestamps before storing in state
        const sanitizedData = sanitizeFirestoreData(contextData);
        setStepContextData(prev => ({ ...prev, [stepId]: sanitizedData }));
      }
    }
  };

  // Helper function to safely format timestamps
  const safeFormatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleString();
    }
    if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      return new Date(timestamp).toLocaleString();
    }
    return 'N/A';
  };

  // Helper function to sanitize Firestore data by converting all timestamps to strings
  const sanitizeFirestoreData = (data) => {
    if (!data || typeof data !== 'object') return data;
    if (Array.isArray(data)) return data.map(sanitizeFirestoreData);

    // Convert Firestore Timestamp to string
    if (data.seconds !== undefined && data.nanoseconds !== undefined) {
      return new Date(data.seconds * 1000).toLocaleString();
    }

    // Recursively sanitize object properties
    const sanitized = {};
    for (const key in data) {
      sanitized[key] = sanitizeFirestoreData(data[key]);
    }
    return sanitized;
  };

  // Define all 11 workflow steps - MATCHING ACTUAL FIRESTORE STRUCTURE
  const workflowSteps = [
    { id: 'job_posted', num: 1, name: 'Job Posted', icon: '📝', type: 'auto' },
    { id: 'find_workers', num: 2, name: 'Find Workers', icon: '🔍', type: 'auto' },
    { id: 'notify_workers', num: 3, name: 'Notify Workers', icon: '🔔', type: 'auto' },
    { id: 'worker_applied', num: 4, name: 'Worker Applied', icon: '👤', type: 'wait' },
    { id: 'worker_approved', num: 5, name: 'Worker Approved', icon: '✅', type: 'wait' },
    { id: 'payment_secured', num: 6, name: 'Payment Secured', icon: '💰', type: 'wait' },
    { id: 'assignment_created', num: 7, name: 'Assignment Created', icon: '📋', type: 'auto' },
    { id: 'work_completed', num: 8, name: 'Work Completed', icon: '✔️', type: 'wait' },
    { id: 'business_confirms', num: 9, name: 'Business Confirms', icon: '👍', type: 'wait' },
    { id: 'payment_released', num: 10, name: 'Payment Released', icon: '💸', type: 'auto' },
    { id: 'payment_received', num: 11, name: 'Payment Received', icon: '🎉', type: 'auto' }
  ];

  // Get status for each step based on ACTUAL STRING STEP IDs
  const getStepStatus = (stepId) => {
    const currentStepId = execution.currentStep; // This is a STRING like "worker_applied"
    const currentStepIndex = workflowSteps.findIndex(s => s.id === currentStepId);
    const thisStepIndex = workflowSteps.findIndex(s => s.id === stepId);

    if (thisStepIndex < 0) return 'pending';
    if (currentStepIndex < 0) return 'pending';

    // Check if this step is completed
    if (execution.steps && execution.steps[stepId]) {
      const stepData = execution.steps[stepId];
      if (stepData.status === 'completed') return 'completed';
      if (stepData.status === 'failed') return 'failed';
      if (stepData.status === 'waiting') return 'waiting';
    }

    if (thisStepIndex < currentStepIndex) return 'completed';
    if (thisStepIndex === currentStepIndex) {
      if (execution.status === 'failed') return 'failed';
      const stepInfo = workflowSteps[thisStepIndex];
      if (stepInfo.type === 'wait') return 'waiting';
      return 'in_progress';
    }
    return 'pending';
  };

  // Get actions/notifications for a step from logs
  const getStepDetails = (stepId) => {
    const stepLogs = logs.filter(log =>
      log.step === stepId || log.step === stepId.toString()
    );

    const stepAI = aiDecisions.filter(ai => {
      // Match AI to step by timing or step reference
      const logTime = stepLogs[0]?.timestamp?.seconds || 0;
      const aiTime = ai.timestamp?.seconds || 0;
      return Math.abs(logTime - aiTime) < 10; // Within 10 seconds
    });

    // Get step data from execution.steps
    const stepData = execution.steps?.[stepId] || null;

    return {
      logs: stepLogs,
      aiDecisions: stepAI,
      notificationsSent: stepLogs.filter(log =>
        log.action?.includes('notification') || log.action?.includes('notify')
      ).length,
      aiAnalyses: stepAI.length,
      stepData: stepData // Include actual step data from execution
    };
  };

  const statusColors = {
    completed: { bg: '#D1FAE5', border: '#10B981', icon: '✅' },
    in_progress: { bg: '#DBEAFE', border: '#3B82F6', icon: '⏳' },
    waiting: { bg: '#FEF3C7', border: '#F59E0B', icon: '⏸️' },
    failed: { bg: '#FEE2E2', border: '#EF4444', icon: '❌' },
    pending: { bg: '#F3F4F6', border: '#D1D5DB', icon: '⚪' }
  };

  return (
    <div style={{
      background: '#F9FAFB',
      borderRadius: '12px',
      padding: '24px',
      overflowX: 'auto'
    }}>
      {/* Workflow Flow Diagram */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        minWidth: '800px'
      }}>
        {workflowSteps.map((step, index) => {
          const status = getStepStatus(step.id);
          const colors = statusColors[status];
          const details = getStepDetails(step.id);
          const isSelected = selectedStep === step.id;

          return (
            <div key={step.id}>
              {/* Step Node */}
              <div
                onClick={() => handleStepClick(step.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {/* Step Number & Icon */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minWidth: '80px'
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: colors.bg,
                    border: `2px solid ${colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: colors.border
                  }}>
                    {step.num}
                  </div>
                  <span style={{ fontSize: '24px' }}>{step.icon}</span>
                </div>

                {/* Step Card */}
                <div style={{
                  flex: 1,
                  background: '#FFFFFF',
                  border: `3px solid ${colors.border}`,
                  borderRadius: '12px',
                  padding: '16px 20px',
                  boxShadow: status === 'in_progress' ? '0 4px 12px rgba(59, 130, 246, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                  transform: status === 'in_progress' ? 'scale(1.02)' : 'scale(1)',
                  transition: 'all 0.2s'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                          {step.name}
                        </h4>
                        <span style={{ fontSize: '18px' }}>{colors.icon}</span>
                      </div>

                      {/* Quick Stats */}
                      <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '13px', color: '#6B7280' }}>
                        {details.notificationsSent > 0 && (
                          <span>🔔 {details.notificationsSent} notification{details.notificationsSent !== 1 ? 's' : ''}</span>
                        )}
                        {details.aiAnalyses > 0 && (
                          <span>🤖 {details.aiAnalyses} AI analysis</span>
                        )}
                        {details.logs.length > 0 && (
                          <span>📋 {details.logs.length} action{details.logs.length !== 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>

                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      background: colors.bg,
                      color: colors.border,
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {status.replace('_', ' ')}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isSelected && (
                    <div style={{
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: '1px solid #E5E7EB'
                    }}>
                      {/* Step Data from Execution */}
                      {details.stepData && (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          background: '#F9FAFB',
                          borderRadius: '6px',
                          fontSize: '13px'
                        }}>
                          <div style={{ fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                            📊 Step Information:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#6B7280' }}>
                            <div>
                              <strong>Status:</strong> <span style={{
                                color: details.stepData.status === 'completed' ? '#10B981' :
                                      details.stepData.status === 'waiting' ? '#F59E0B' : '#3B82F6',
                                fontWeight: '600',
                                textTransform: 'uppercase',
                                fontSize: '12px'
                              }}>{details.stepData.status}</span>
                            </div>
                            {details.stepData.startedAt && (
                              <div>
                                <strong>Started:</strong> {safeFormatTimestamp(details.stepData.startedAt)}
                              </div>
                            )}
                            {details.stepData.completedAt && (
                              <div>
                                <strong>Completed:</strong> {safeFormatTimestamp(details.stepData.completedAt)}
                              </div>
                            )}
                            {details.stepData.waitingFor && (
                              <div>
                                <strong>Waiting for:</strong> <span style={{ color: '#F59E0B' }}>{details.stepData.waitingFor}</span>
                              </div>
                            )}
                            {details.stepData.workersNotified && (
                              <div>
                                <strong>Workers Notified:</strong> {details.stepData.workersNotified}
                              </div>
                            )}
                            {details.stepData.workerId && (
                              <div>
                                <strong>Worker ID:</strong> {details.stepData.workerId}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Contextual Data - Job Details */}
                      {stepContextData[step.id]?.job && (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          background: '#FFFBEB',
                          borderRadius: '6px',
                          fontSize: '13px',
                          border: '1px solid #FCD34D'
                        }}>
                          <div style={{ fontWeight: '600', color: '#92400E', marginBottom: '8px', fontSize: '14px' }}>
                            📄 Job Details:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#78350F' }}>
                            <div><strong>Title:</strong> {stepContextData[step.id].job.jobTitle || stepContextData[step.id].job.title || 'N/A'}</div>
                            <div><strong>Category:</strong> {stepContextData[step.id].job.category || 'N/A'}</div>
                            <div><strong>Location:</strong> {typeof stepContextData[step.id].job.location === 'object' ? (stepContextData[step.id].job.location?.city || stepContextData[step.id].job.location?.address || JSON.stringify(stepContextData[step.id].job.location)) : (stepContextData[step.id].job.location || 'N/A')}</div>
                            <div><strong>Rate:</strong> RM {stepContextData[step.id].job.hourlyRate || 0}/{stepContextData[step.id].job.paymentType === 'per_job' ? 'job' : stepContextData[step.id].job.paymentType === 'per_day' ? 'day' : 'hr'}</div>
                            <div><strong>Workers Needed:</strong> {stepContextData[step.id].job.workersNeeded || 0}</div>
                            {stepContextData[step.id].job.description && (
                              <div><strong>Description:</strong> <div style={{ marginTop: '4px', padding: '8px', background: '#FEF3C7', borderRadius: '4px' }}>{String(stepContextData[step.id].job.description)}</div></div>
                            )}
                            {stepContextData[step.id].job.requiredSkills && Array.isArray(stepContextData[step.id].job.requiredSkills) && stepContextData[step.id].job.requiredSkills.length > 0 && (
                              <div><strong>Required Skills:</strong> {stepContextData[step.id].job.requiredSkills.join(', ')}</div>
                            )}
                            {stepContextData[step.id].job.startDate && (
                              <div><strong>Start Date:</strong> {safeFormatTimestamp(stepContextData[step.id].job.startDate)}</div>
                            )}
                            {(stepContextData[step.id].job.startTime || stepContextData[step.id].job.endTime) && (
                              <div><strong>Time:</strong> {stepContextData[step.id].job.startTime || 'N/A'} - {stepContextData[step.id].job.endTime || 'N/A'}</div>
                            )}
                            {stepContextData[step.id].job.availableWorkers && Array.isArray(stepContextData[step.id].job.availableWorkers) && (
                              <div><strong>Available Workers:</strong> {stepContextData[step.id].job.availableWorkers.length} matched</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Contextual Data - Worker Details */}
                      {stepContextData[step.id]?.worker && (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          background: '#EFF6FF',
                          borderRadius: '6px',
                          fontSize: '13px',
                          border: '1px solid #93C5FD'
                        }}>
                          <div style={{ fontWeight: '600', color: '#1E40AF', marginBottom: '8px', fontSize: '14px' }}>
                            👤 Worker Details:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#1E3A8A' }}>
                            <div><strong>Name:</strong> {stepContextData[step.id].worker.personalInfo?.name || 'N/A'}</div>
                            <div><strong>Email:</strong> {stepContextData[step.id].worker.email}</div>
                            <div><strong>Phone:</strong> {stepContextData[step.id].worker.personalInfo?.phoneNumber || 'N/A'}</div>
                            {stepContextData[step.id].worker.workExperience && stepContextData[step.id].worker.workExperience.length > 0 && (
                              <div><strong>Experience:</strong> {stepContextData[step.id].worker.workExperience.map(exp => exp.jobTitle || exp.title).join(', ')}</div>
                            )}
                            {stepContextData[step.id].worker.skills && stepContextData[step.id].worker.skills.length > 0 && (
                              <div><strong>Skills:</strong> {stepContextData[step.id].worker.skills.join(', ')}</div>
                            )}
                            {stepContextData[step.id].worker.rating && (
                              <div><strong>Rating:</strong> ⭐ {stepContextData[step.id].worker.rating}/5</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Contextual Data - Payment Details */}
                      {stepContextData[step.id]?.payment && (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          background: '#DCFCE7',
                          borderRadius: '6px',
                          fontSize: '13px',
                          border: '1px solid #86EFAC'
                        }}>
                          <div style={{ fontWeight: '600', color: '#166534', marginBottom: '8px', fontSize: '14px' }}>
                            💰 Payment Details:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#14532D' }}>
                            <div><strong>Amount:</strong> RM {stepContextData[step.id].payment.amount}</div>
                            <div><strong>Status:</strong> <span style={{
                              color: stepContextData[step.id].payment.status === 'escrowed' ? '#10B981' : '#F59E0B',
                              fontWeight: '600',
                              textTransform: 'uppercase'
                            }}>{stepContextData[step.id].payment.status}</span></div>
                            {stepContextData[step.id].payment.toyyibpayBillCode && (
                              <div><strong>Bill Code:</strong> {String(stepContextData[step.id].payment.toyyibpayBillCode)}</div>
                            )}
                            {stepContextData[step.id].payment.createdAt && (
                              <div><strong>Created:</strong> {safeFormatTimestamp(stepContextData[step.id].payment.createdAt)}</div>
                            )}
                            {stepContextData[step.id].payment.releasedAt && (
                              <div><strong>Released:</strong> {safeFormatTimestamp(stepContextData[step.id].payment.releasedAt)}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Contextual Data - Assignment Details */}
                      {stepContextData[step.id]?.assignment && (
                        <div style={{
                          marginBottom: '16px',
                          padding: '12px',
                          background: '#F3E8FF',
                          borderRadius: '6px',
                          fontSize: '13px',
                          border: '1px solid #D8B4FE'
                        }}>
                          <div style={{ fontWeight: '600', color: '#7E22CE', marginBottom: '8px', fontSize: '14px' }}>
                            📋 Assignment Details:
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', color: '#6B21A8' }}>
                            <div><strong>Status:</strong> <span style={{
                              fontWeight: '600',
                              textTransform: 'uppercase'
                            }}>{String(stepContextData[step.id].assignment.status || 'N/A')}</span></div>
                            {stepContextData[step.id].assignment.startDate && (
                              <div><strong>Start Date:</strong> {safeFormatTimestamp(stepContextData[step.id].assignment.startDate)}</div>
                            )}
                            {(stepContextData[step.id].assignment.startTime || stepContextData[step.id].assignment.endTime) && (
                              <div><strong>Time:</strong> {String(stepContextData[step.id].assignment.startTime || 'N/A')} - {String(stepContextData[step.id].assignment.endTime || 'N/A')}</div>
                            )}
                            {stepContextData[step.id].assignment.actualStartTime && (
                              <div><strong>Actual Start:</strong> {String(stepContextData[step.id].assignment.actualStartTime)}</div>
                            )}
                            {stepContextData[step.id].assignment.actualEndTime && (
                              <div><strong>Actual End:</strong> {String(stepContextData[step.id].assignment.actualEndTime)}</div>
                            )}
                            {stepContextData[step.id].assignment.completedAt && (
                              <div><strong>Completed:</strong> {safeFormatTimestamp(stepContextData[step.id].assignment.completedAt)}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {details.logs.length > 0 && (
                        <>
                          <div style={{ marginBottom: '12px' }}>
                            <strong style={{ fontSize: '13px', color: '#374151' }}>Actions Performed:</strong>
                          </div>

                      {/* Actions List */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {step.actions.map((action, idx) => {
                          const actionLog = details.logs.find(log =>
                            log.action === action || log.details?.includes(action)
                          );
                          const performed = !!actionLog;

                          return (
                            <div key={idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 12px',
                              background: performed ? '#F0FDF4' : '#F9FAFB',
                              borderRadius: '6px',
                              fontSize: '13px'
                            }}>
                              <span>{performed ? '✅' : '⏸️'}</span>
                              <span style={{
                                fontFamily: 'monospace',
                                color: performed ? '#166534' : '#6B7280'
                              }}>
                                {action}
                              </span>
                              {actionLog && actionLog.status === 'error' && (
                                <span style={{ color: '#DC2626', fontSize: '12px' }}>
                                  (Failed)
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Notifications Details */}
                      {details.notificationsSent > 0 && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#FFF7ED', borderRadius: '6px' }}>
                          <div style={{ fontSize: '12px', color: '#92400E', marginBottom: '4px', fontWeight: '600' }}>
                            📨 Notifications Sent:
                          </div>
                          {details.logs
                            .filter(log => log.action?.includes('notification') || log.action?.includes('notify'))
                            .map((notifLog, idx) => (
                              <div key={idx} style={{ fontSize: '12px', color: '#78350F', marginTop: '4px' }}>
                                • {notifLog.action} {notifLog.details && `- ${typeof notifLog.details === 'string' ? notifLog.details : 'Sent'}`}
                              </div>
                            ))}
                        </div>
                      )}

                      {/* AI Results */}
                      {details.aiDecisions.length > 0 && (
                        <div style={{ marginTop: '12px', padding: '12px', background: '#F0F9FF', borderRadius: '6px' }}>
                          <div style={{ fontSize: '12px', color: '#1E40AF', marginBottom: '8px', fontWeight: '600' }}>
                            🤖 AI Analysis Results:
                          </div>
                          {details.aiDecisions.map((ai, idx) => (
                            <div key={idx} style={{ fontSize: '12px', color: '#1E3A8A', marginTop: '4px' }}>
                              <strong>{ai.analysisType}:</strong>
                              {ai.result && typeof ai.result === 'object' && ai.result.score && (
                                <span> Score {ai.result.score}/100</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Connector Arrow */}
              {index < workflowSteps.length - 1 && (
                <div style={{
                  marginLeft: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px'
                }}>
                  <div style={{
                    width: '3px',
                    height: '100%',
                    background: getStepStatus(step.id) === 'completed' ? '#10B981' : '#D1D5DB'
                  }} />
                  <div style={{
                    marginLeft: '-3px',
                    width: '0',
                    height: '0',
                    borderLeft: '6px solid ' + (getStepStatus(step.id) === 'completed' ? '#10B981' : '#D1D5DB'),
                    borderTop: '4px solid transparent',
                    borderBottom: '4px solid transparent'
                  }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: '#FFFFFF',
        borderRadius: '8px',
        display: 'flex',
        gap: '24px',
        flexWrap: 'wrap',
        fontSize: '13px'
      }}>
        <div><span style={{ color: '#10B981', fontWeight: '600' }}>✅ Completed</span> - Step finished successfully</div>
        <div><span style={{ color: '#3B82F6', fontWeight: '600' }}>⏳ In Progress</span> - Currently executing</div>
        <div><span style={{ color: '#EF4444', fontWeight: '600' }}>❌ Failed</span> - Error occurred</div>
        <div><span style={{ color: '#9CA3AF', fontWeight: '600' }}>⏸️ Pending</span> - Not started yet</div>
      </div>

      <div style={{ marginTop: '12px', fontSize: '13px', color: '#6B7280', fontStyle: 'italic' }}>
        💡 Click any step card to see actions, notifications, and AI analysis details
      </div>
    </div>
  );
}

// Job Progression Tab - User-friendly view of job status and payment flow
function JobProgressionTab() {
  const [jobs, setJobs] = React.useState([]);
  const [escrowPayments, setEscrowPayments] = React.useState([]);
  const [assignments, setAssignments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expandedJob, setExpandedJob] = React.useState(null);
  const [summary, setSummary] = React.useState({ pending: 0, escrowed: 0, released: 0, paid: 0 });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch all jobs (active, completed, filled)
        const jobsQuery = query(
          collection(db, 'staffingRequests'),
          orderBy('createdAt', 'desc'),
          limit(50)
        );
        const jobsSnap = await getDocs(jobsQuery);
        const jobsList = jobsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setJobs(jobsList);

        // Fetch all escrow payments
        const escrowQuery = query(
          collection(db, 'escrowPayments'),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
        const escrowSnap = await getDocs(escrowQuery);
        const escrowList = escrowSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setEscrowPayments(escrowList);

        // Fetch active assignments
        const assignQuery = query(
          collection(db, 'activeAssignments'),
          orderBy('createdAt', 'desc'),
          limit(100)
        );
        const assignSnap = await getDocs(assignQuery);
        const assignList = assignSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAssignments(assignList);

        // Calculate summary
        let pendingTotal = 0, escrowedTotal = 0, releasedTotal = 0, paidTotal = 0;
        escrowList.forEach(e => {
          const amt = e.amount || 0;
          if (e.status === 'pending') pendingTotal += amt;
          else if (e.status === 'escrowed') escrowedTotal += amt;
          else if (['released', 'completed', 'payout_initiated'].includes(e.status)) releasedTotal += amt;
          else if (e.status === 'paid') paidTotal += amt;
        });
        setSummary({ pending: pendingTotal, escrowed: escrowedTotal, released: releasedTotal, paid: paidTotal });

      } catch (error) {
        console.error('Error fetching job data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString('en-MY', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getJobEscrows = (jobId) => escrowPayments.filter(e => e.staffingRequestId === jobId);
  const getJobAssignments = (jobId) => assignments.filter(a => a.staffingRequestId === jobId);

  const getJobStage = (job) => {
    const escrows = getJobEscrows(job.id);
    const assigns = getJobAssignments(job.id);
    const hasPaid = escrows.some(e => e.status === 'paid');
    const hasReleased = escrows.some(e => ['released', 'completed', 'payout_initiated'].includes(e.status));
    const hasEscrowed = escrows.some(e => e.status === 'escrowed');
    const approvedCount = job.approvedWorkers?.length || 0;
    const pendingCount = job.pendingWorkers?.length || 0;

    if (hasPaid) return { stage: 6, label: 'PAID OUT', color: '#059669', bg: '#D1FAE5' };
    if (hasReleased) return { stage: 5, label: 'COMPLETED', color: '#10B981', bg: '#D1FAE5' };
    if (hasEscrowed && assigns.length > 0) return { stage: 4, label: 'IN PROGRESS', color: '#3B82F6', bg: '#DBEAFE' };
    if (approvedCount > 0) return { stage: 3, label: 'WORKERS HIRED', color: '#8B5CF6', bg: '#EDE9FE' };
    if (pendingCount > 0) return { stage: 2, label: 'WORKERS APPLIED', color: '#F59E0B', bg: '#FEF3C7' };
    return { stage: 1, label: 'POSTED', color: '#6B7280', bg: '#F3F4F6' };
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>
        <p style={{ fontSize: '18px' }}>Loading job progression data...</p>
      </div>
    );
  }

  return (
    <div>
      <h2>📊 Job Progression</h2>
      <p style={{ color: '#6B7280', marginBottom: '24px' }}>
        Track the status of all active jobs from posting to payment release.
      </p>

      {/* Payment Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <div style={{ background: '#FEF3C7', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#D97706' }}>RM{summary.pending.toFixed(2)}</div>
          <div style={{ fontSize: '14px', color: '#92400E', marginTop: '4px' }}>Pending Payment</div>
        </div>
        <div style={{ background: '#DBEAFE', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#2563EB' }}>RM{summary.escrowed.toFixed(2)}</div>
          <div style={{ fontSize: '14px', color: '#1E40AF', marginTop: '4px' }}>In Escrow (Held)</div>
        </div>
        <div style={{ background: '#FEF3C7', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#B45309' }}>RM{summary.released.toFixed(2)}</div>
          <div style={{ fontSize: '14px', color: '#78350F', marginTop: '4px' }}>Released (Pending Payout)</div>
        </div>
        <div style={{ background: '#D1FAE5', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#059669' }}>RM{summary.paid.toFixed(2)}</div>
          <div style={{ fontSize: '14px', color: '#065F46', marginTop: '4px' }}>Paid to Workers</div>
        </div>
      </div>

      {/* Job Flow Legend */}
      <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px', marginBottom: '24px' }}>
        <div style={{ fontWeight: '600', marginBottom: '12px', color: '#374151' }}>Job Flow:</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ padding: '6px 12px', background: '#F3F4F6', borderRadius: '20px', fontSize: '13px' }}>1. Posted</span>
          <span style={{ color: '#9CA3AF' }}>→</span>
          <span style={{ padding: '6px 12px', background: '#FEF3C7', borderRadius: '20px', fontSize: '13px' }}>2. Workers Apply</span>
          <span style={{ color: '#9CA3AF' }}>→</span>
          <span style={{ padding: '6px 12px', background: '#EDE9FE', borderRadius: '20px', fontSize: '13px' }}>3. Hire & Pay</span>
          <span style={{ color: '#9CA3AF' }}>→</span>
          <span style={{ padding: '6px 12px', background: '#DBEAFE', borderRadius: '20px', fontSize: '13px' }}>4. Work In Progress</span>
          <span style={{ color: '#9CA3AF' }}>→</span>
          <span style={{ padding: '6px 12px', background: '#FEF3C7', borderRadius: '20px', fontSize: '13px' }}>5. Released</span>
          <span style={{ color: '#9CA3AF' }}>→</span>
          <span style={{ padding: '6px 12px', background: '#D1FAE5', borderRadius: '20px', fontSize: '13px' }}>6. Paid Out</span>
        </div>
      </div>

      {/* Jobs List */}
      <h3 style={{ marginBottom: '16px' }}>Jobs ({jobs.length})</h3>

      {jobs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', background: '#F9FAFB', borderRadius: '12px', color: '#6B7280' }}>
          <p>No active jobs found</p>
        </div>
      ) : (
        jobs.map(job => {
          const stage = getJobStage(job);
          const escrows = getJobEscrows(job.id);
          const assigns = getJobAssignments(job.id);
          const isExpanded = expandedJob === job.id;
          const pendingWorkers = job.pendingWorkers || [];
          const approvedWorkers = job.approvedWorkers || [];

          return (
            <div key={job.id} style={{
              background: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              marginBottom: '16px',
              overflow: 'hidden'
            }}>
              {/* Job Header */}
              <div
                onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                style={{
                  padding: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '18px' }}>{isExpanded ? '▼' : '▶'}</span>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>{job.jobTitle || job.title || 'Untitled Job'}</h3>
                    <span style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: stage.bg,
                      color: stage.color
                    }}>
                      {stage.label}
                    </span>
                  </div>
                  <p style={{ margin: '8px 0 0 30px', color: '#6B7280', fontSize: '14px' }}>
                    Created: {formatDate(job.createdAt)} • {pendingWorkers.length} pending, {approvedWorkers.length} hired
                  </p>
                </div>

                {/* Quick Stats */}
                <div style={{ display: 'flex', gap: '16px', marginRight: '20px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#F59E0B' }}>{pendingWorkers.length}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>Applied</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#10B981' }}>{approvedWorkers.length}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>Hired</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#3B82F6' }}>
                      RM{escrows.filter(e => e.status === 'escrowed').reduce((sum, e) => sum + (e.amount || 0), 0)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>Escrowed</div>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div style={{ borderTop: '1px solid #E5E7EB', padding: '20px', background: '#F9FAFB' }}>
                  {/* Progress Bar */}
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      {[1, 2, 3, 4, 5, 6].map(s => (
                        <div key={s} style={{
                          flex: 1,
                          textAlign: 'center',
                          padding: '8px',
                          background: s <= stage.stage ? stage.bg : '#E5E7EB',
                          color: s <= stage.stage ? stage.color : '#9CA3AF',
                          fontWeight: s === stage.stage ? '700' : '400',
                          fontSize: '12px',
                          borderRadius: s === 1 ? '8px 0 0 8px' : s === 6 ? '0 8px 8px 0' : '0'
                        }}>
                          {s === 1 && 'Posted'}
                          {s === 2 && 'Applied'}
                          {s === 3 && 'Hired'}
                          {s === 4 && 'Working'}
                          {s === 5 && 'Released'}
                          {s === 6 && 'Paid'}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {/* Pending Workers */}
                    <div style={{ background: '#FFF', padding: '16px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#F59E0B' }}>⏳ Pending Workers ({pendingWorkers.length})</h4>
                      {pendingWorkers.length === 0 ? (
                        <p style={{ color: '#9CA3AF', fontSize: '14px' }}>No pending workers</p>
                      ) : (
                        pendingWorkers.map((w, i) => {
                          const name = typeof w === 'object' ? w.workerName : 'Worker';
                          return (
                            <div key={i} style={{ padding: '8px 0', borderBottom: i < pendingWorkers.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                              <span style={{ fontSize: '14px' }}>{name || 'Worker'}</span>
                            </div>
                          );
                        })
                      )}
                    </div>

                    {/* Hired Workers */}
                    <div style={{ background: '#FFF', padding: '16px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#10B981' }}>✅ Hired Workers ({approvedWorkers.length})</h4>
                      {approvedWorkers.length === 0 ? (
                        <p style={{ color: '#9CA3AF', fontSize: '14px' }}>No workers hired yet</p>
                      ) : (
                        approvedWorkers.map((w, i) => {
                          const name = typeof w === 'object' ? w.workerName : 'Worker';
                          return (
                            <div key={i} style={{ padding: '8px 0', borderBottom: i < approvedWorkers.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                              <span style={{ fontSize: '14px' }}>{name || 'Worker'}</span>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Escrow Payments */}
                  <div style={{ background: '#FFF', padding: '16px', borderRadius: '8px', border: '1px solid #E5E7EB', marginTop: '16px' }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#3B82F6' }}>💰 Escrow Payments ({escrows.length})</h4>
                    {escrows.length === 0 ? (
                      <p style={{ color: '#9CA3AF', fontSize: '14px' }}>No payments made yet</p>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                          <tr style={{ background: '#F9FAFB' }}>
                            <th style={{ padding: '10px', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>Worker</th>
                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>Amount</th>
                            <th style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {escrows.map((e, i) => (
                            <tr key={i}>
                              <td style={{ padding: '10px', borderBottom: '1px solid #F3F4F6' }}>{e.workerName || 'Worker'}</td>
                              <td style={{ padding: '10px', textAlign: 'right', borderBottom: '1px solid #F3F4F6', fontWeight: '600' }}>RM{e.amount || 0}</td>
                              <td style={{ padding: '10px', textAlign: 'center', borderBottom: '1px solid #F3F4F6' }}>
                                <span style={{
                                  padding: '4px 10px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  background: e.status === 'paid' ? '#D1FAE5' : e.status === 'payout_initiated' ? '#DBEAFE' : e.status === 'completed' ? '#E0E7FF' : e.status === 'escrowed' ? '#DBEAFE' : e.status === 'released' ? '#FEF3C7' : e.status === 'pending' ? '#FEF3C7' : '#F3F4F6',
                                  color: e.status === 'paid' ? '#065F46' : e.status === 'payout_initiated' ? '#1E40AF' : e.status === 'completed' ? '#3730A3' : e.status === 'escrowed' ? '#1E40AF' : e.status === 'released' ? '#92400E' : e.status === 'pending' ? '#92400E' : '#6B7280'
                                }}>
                                  {e.status === 'paid' ? '✅ Paid' : e.status === 'payout_initiated' ? '🔄 Payout Processing' : e.status === 'completed' ? '☑️ Completed' : e.status === 'escrowed' ? '💰 In Escrow' : e.status === 'released' ? '📤 Released' : e.status === 'pending' ? '⏳ Pending' : e.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Next Action */}
                  <div style={{ marginTop: '16px', padding: '16px', background: '#FEF3C7', borderRadius: '8px' }}>
                    <strong style={{ color: '#92400E' }}>📋 Next Action: </strong>
                    <span style={{ color: '#78350F' }}>
                      {stage.stage === 1 && 'Waiting for workers to apply'}
                      {stage.stage === 2 && 'Review pending workers and hire them'}
                      {stage.stage === 3 && 'Complete payment to escrow funds'}
                      {stage.stage === 4 && 'Worker completes job → Business confirms → Payment releases'}
                      {stage.stage === 5 && 'Payment released! Process payout to worker via Billplz.'}
                      {stage.stage === 6 && 'All done! Payment has been sent to worker\'s bank account.'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// Combined Rules & Config Tab (Notifications + Banners + Status Rules)
function RulesConfigTab({ platformConfig }) {
  const [activeSubTab, setActiveSubTab] = useState('notifications');

  const notificationRules = platformConfig.filter(c => c.type === 'notificationRule');
  const bannerRules = platformConfig.filter(c => c.type === 'bannerRule');
  const statusRules = platformConfig.filter(c => c.type === 'statusRule');

  return (
    <div>
      <h2>📜 Rules & Configuration</h2>
      <p style={{ color: '#6B7280', marginBottom: '16px' }}>
        Manage notification rules, banners, and status transitions. All rules loaded from Firestore platformConfig.
      </p>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #E5E7EB', paddingBottom: '12px' }}>
        <button
          onClick={() => setActiveSubTab('notifications')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: activeSubTab === 'notifications' ? '#3B82F6' : '#F3F4F6',
            color: activeSubTab === 'notifications' ? 'white' : '#374151',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          🔔 Notifications ({notificationRules.length})
        </button>
        <button
          onClick={() => setActiveSubTab('banners')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: activeSubTab === 'banners' ? '#3B82F6' : '#F3F4F6',
            color: activeSubTab === 'banners' ? 'white' : '#374151',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          📢 Banners ({bannerRules.length})
        </button>
        <button
          onClick={() => setActiveSubTab('status')}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: activeSubTab === 'status' ? '#3B82F6' : '#F3F4F6',
            color: activeSubTab === 'status' ? 'white' : '#374151',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          🔄 Status Rules ({statusRules.length})
        </button>
      </div>

      {/* Content based on sub-tab */}
      {activeSubTab === 'notifications' && (
        <NotificationsSubTab rules={notificationRules} />
      )}
      {activeSubTab === 'banners' && (
        <BannersSubTab rules={bannerRules} />
      )}
      {activeSubTab === 'status' && (
        <StatusRulesSubTab rules={statusRules} />
      )}
    </div>
  );
}

// Sub-tab components for RulesConfigTab
function NotificationsSubTab({ rules }) {
  if (rules.length === 0) {
    return <div style={styles.emptyState}><p>No notification rules configured.</p></div>;
  }
  return (
    <div>
      {rules.map(rule => (
        <div key={rule.id} style={{ ...styles.jobCard, marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ margin: 0 }}>{rule.name || rule.id}</h4>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>
                {rule.active ? '🟢 Active' : '🔴 Inactive'} • {rule.notificationType || 'push'}
              </p>
            </div>
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{rule.priority || 'Normal'}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function BannersSubTab({ rules }) {
  if (rules.length === 0) {
    return <div style={styles.emptyState}><p>No banner rules configured.</p></div>;
  }
  return (
    <div>
      {rules.map(rule => (
        <div key={rule.id} style={{ ...styles.jobCard, marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ margin: 0 }}>{rule.name || rule.id}</h4>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>
                {rule.active ? '🟢 Active' : '🔴 Inactive'} • {rule.bannerType || 'info'}
              </p>
            </div>
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{rule.targetScreen || 'All'}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusRulesSubTab({ rules }) {
  if (rules.length === 0) {
    return <div style={styles.emptyState}><p>No status rules configured.</p></div>;
  }
  return (
    <div>
      {rules.map(rule => (
        <div key={rule.id} style={{ ...styles.jobCard, marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ margin: 0 }}>{rule.name || rule.id}</h4>
              <p style={{ fontSize: '13px', color: '#6B7280', margin: '4px 0 0' }}>
                {rule.active ? '🟢 Active' : '🔴 Inactive'} • {rule.entityType || 'assignment'}
              </p>
            </div>
            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{rule.transitions?.length || 0} transitions</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Notifications Tab (Notification Rules Management)
function NotificationsTab({ platformConfig }) {
  const notificationRules = platformConfig.filter(c => c.type === 'notificationRule');

  return (
    <div>
      <h2>🔔 Notification Rules ({notificationRules.length})</h2>
      <p style={{ color: '#6B7280', marginBottom: '24px' }}>
        Manage when and how users receive notifications. All rules are dynamically loaded from Firestore.
      </p>

      <div style={{ marginTop: '24px' }}>
        {notificationRules.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>No notification rules found</h3>
            <p>Notification rules will appear here once they are created in Firestore platformConfig collection.</p>
          </div>
        ) : (
          notificationRules.map(rule => (
            <div key={rule.id} style={styles.jobCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <h3>{rule.name || rule.id}</h3>
                  <p style={styles.cardSubtitle}>
                    {rule.active ? '🟢 Active' : '🔴 Inactive'} • Priority: {rule.priority || 'Normal'} • Type: {rule.notificationType || 'push'}
                  </p>
                </div>
                <div style={{
                  padding: '8px 16px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: rule.active ? '#D1FAE5' : '#FEE2E2',
                  color: rule.active ? '#065F46' : '#991B1B'
                }}>
                  {rule.active ? 'Active' : 'Inactive'}
                </div>
              </div>
              {rule.description && <p style={{ marginBottom: '12px' }}>{rule.description}</p>}
              {rule.trigger && (
                <div style={{ marginTop: '12px' }}>
                  <strong>Trigger Conditions:</strong>
                  <pre style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#F3F4F6',
                    borderRadius: '6px',
                    fontSize: '13px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(rule.trigger, null, 2)}
                  </pre>
                </div>
              )}
              {rule.template && (
                <div style={{ marginTop: '12px' }}>
                  <strong>Message Template:</strong>
                  <p style={{ marginTop: '8px', fontSize: '14px', color: '#6B7280' }}>
                    {rule.template.message || 'N/A'}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Banners Tab (Banner Rules Management)
function BannersTab({ platformConfig }) {
  const bannerRules = platformConfig.filter(c => c.type === 'bannerRule');

  return (
    <div>
      <h2>📢 Banner Rules ({bannerRules.length})</h2>
      <p style={{ color: '#6B7280', marginBottom: '24px' }}>
        Manage in-app banners shown to users based on conditions. All rules are dynamically loaded from Firestore.
      </p>

      <div style={{ marginTop: '24px' }}>
        {bannerRules.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>No banner rules found</h3>
            <p>Banner rules will appear here once they are created in Firestore platformConfig collection.</p>
          </div>
        ) : (
          bannerRules.map(rule => (
            <div key={rule.id} style={styles.jobCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <h3>{rule.name || rule.id}</h3>
                  <p style={styles.cardSubtitle}>
                    {rule.active ? '🟢 Active' : '🔴 Inactive'} • Type: {rule.bannerType || 'info'} • Screen: {rule.screen || 'All'}
                  </p>
                </div>
                <div style={{
                  padding: '8px 16px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: rule.bannerType === 'error' ? '#FEE2E2' :
                             rule.bannerType === 'warning' ? '#FEF3C7' :
                             rule.bannerType === 'success' ? '#D1FAE5' : '#DBEAFE',
                  color: rule.bannerType === 'error' ? '#991B1B' :
                         rule.bannerType === 'warning' ? '#92400E' :
                         rule.bannerType === 'success' ? '#065F46' : '#1E40AF'
                }}>
                  {rule.bannerType || 'info'}
                </div>
              </div>
              {rule.description && <p style={{ marginBottom: '12px' }}>{rule.description}</p>}
              {rule.message && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  background: '#F9FAFB',
                  borderRadius: '8px',
                  borderLeft: '4px solid #FD7F3A'
                }}>
                  <strong>Banner Message:</strong>
                  <p style={{ marginTop: '8px' }}>{rule.message}</p>
                </div>
              )}
              {rule.conditions && (
                <div style={{ marginTop: '12px' }}>
                  <strong>Display Conditions:</strong>
                  <pre style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#F3F4F6',
                    borderRadius: '6px',
                    fontSize: '13px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(rule.conditions, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Status Rules Tab (State Machine Management)
function StatusRulesTab({ platformConfig }) {
  const statusRules = platformConfig.filter(c => c.type === 'statusRule');

  return (
    <div>
      <h2>🔄 Status Rules ({statusRules.length})</h2>
      <p style={{ color: '#6B7280', marginBottom: '24px' }}>
        Manage state transitions for jobs, assignments, and payments. All rules are dynamically loaded from Firestore.
      </p>

      <div style={{ marginTop: '24px' }}>
        {statusRules.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>No status rules found</h3>
            <p>Status rules will appear here once they are created in Firestore platformConfig collection.</p>
          </div>
        ) : (
          statusRules.map(rule => (
            <div key={rule.id} style={styles.jobCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div>
                  <h3>{rule.name || rule.id}</h3>
                  <p style={styles.cardSubtitle}>
                    {rule.active ? '🟢 Active' : '🔴 Inactive'} • Entity: {rule.entityType || 'Unknown'} • Collection: {rule.collection || 'N/A'}
                  </p>
                </div>
                <div style={{
                  padding: '8px 16px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '600',
                  background: rule.active ? '#D1FAE5' : '#FEE2E2',
                  color: rule.active ? '#065F46' : '#991B1B'
                }}>
                  {rule.active ? 'Active' : 'Inactive'}
                </div>
              </div>
              {rule.description && <p style={{ marginBottom: '12px' }}>{rule.description}</p>}
              {rule.transitions && (
                <div style={{ marginTop: '12px' }}>
                  <strong>State Transitions ({Object.keys(rule.transitions).length} defined):</strong>
                  <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
                    {Object.entries(rule.transitions).map(([fromState, toStates]) => (
                      <div key={fromState} style={{
                        padding: '12px',
                        background: '#F9FAFB',
                        borderRadius: '6px',
                        borderLeft: '3px solid #FD7F3A'
                      }}>
                        <strong>{fromState}</strong> → {Array.isArray(toStates) ? toStates.join(', ') : toStates}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rule.validations && (
                <div style={{ marginTop: '12px' }}>
                  <strong>Validation Rules:</strong>
                  <pre style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#F3F4F6',
                    borderRadius: '6px',
                    fontSize: '13px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(rule.validations, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Audit Logs Tab (Activity History - Not Hardcoded)
function AuditLogsTab({ logs }) {
  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div>
      <h2>Audit Logs ({logs.length})</h2>
      <p style={{ color: '#6B7280', marginBottom: '24px' }}>
        Complete system activity history. All actions are logged to Firestore auditLogs collection.
      </p>

      <div style={{ marginTop: '24px' }}>
        {logs.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>No audit logs found</h3>
            <p>System activity logs will appear here once actions are performed.</p>
          </div>
        ) : (
          logs.map((log, index) => (
            <div key={log.id || index} style={{
              ...styles.jobCard,
              borderLeft: '4px solid ' + (
                log.severity === 'error' ? '#EF4444' :
                log.severity === 'warning' ? '#F59E0B' :
                log.severity === 'success' ? '#10B981' :
                '#3B82F6'
              )
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <strong>{log.eventName || 'Unknown Event'}</strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {log.severity && (
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: log.severity === 'error' ? '#FEE2E2' :
                                 log.severity === 'warning' ? '#FEF3C7' :
                                 log.severity === 'success' ? '#D1FAE5' : '#DBEAFE',
                      color: log.severity === 'error' ? '#991B1B' :
                             log.severity === 'warning' ? '#92400E' :
                             log.severity === 'success' ? '#065F46' : '#1E40AF'
                    }}>
                      {log.severity.toUpperCase()}
                    </span>
                  )}
                  <span style={{ fontSize: '13px', color: '#6B7280' }}>
                    {formatDate(log.timestamp)}
                  </span>
                </div>
              </div>
              {log.executionId && (
                <p style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                  <strong>Execution ID:</strong> {log.executionId}
                </p>
              )}
              {log.metadata && (
                <details style={{ marginTop: '8px', cursor: 'pointer' }}>
                  <summary style={{ color: '#6B7280', fontSize: '13px' }}>View Metadata</summary>
                  <pre style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#F3F4F6',
                    borderRadius: '6px',
                    fontSize: '12px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(log.metadata, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// AI Dashboard Tab
function AIDashboardTab({ decisions, evolutions, metrics, onApprove, onReject }) {
  const [selectedEvolution, setSelectedEvolution] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('pending'); // pending, decisions, all

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleApprove = async () => {
    if (!selectedEvolution) return;

    if (!window.confirm('Approve this AI suggestion and deploy it?')) return;

    setProcessing(true);
    try {
      await onApprove(selectedEvolution.id, adminNotes);
      alert('✅ AI Suggestion Approved!');
      setSelectedEvolution(null);
      setAdminNotes('');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedEvolution || !rejectionReason) {
      alert('Please select a rejection reason');
      return;
    }

    if (!window.confirm('Reject this AI suggestion?')) return;

    setProcessing(true);
    try {
      await onReject(selectedEvolution.id, rejectionReason, adminNotes);
      alert('AI Suggestion Rejected');
      setSelectedEvolution(null);
      setAdminNotes('');
      setRejectionReason('');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Filter evolutions by status
  const pendingEvolutions = evolutions.filter(e => e.status === 'pending');
  const approvedEvolutions = evolutions.filter(e => e.status === 'approved');
  const rejectedEvolutions = evolutions.filter(e => e.status === 'rejected');

  // Modal for reviewing AI suggestion
  if (selectedEvolution) {
    return (
      <div style={styles.reviewModal}>
        <div style={styles.modalHeader}>
          <h2>Review AI Suggestion</h2>
          <button onClick={() => setSelectedEvolution(null)} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.modalContent}>
          {/* AI Suggestion Details */}
          <div style={styles.section}>
            <h3>🤖 AI Suggestion</h3>
            <table style={styles.dataTable}>
              <tbody>
                <tr>
                  <td style={styles.dataLabel}>Rule Type:</td>
                  <td style={styles.dataValue}>{selectedEvolution.ruleType || 'N/A'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Confidence:</td>
                  <td style={styles.dataValue}>
                    <strong style={{ color: (selectedEvolution.confidence || 0) >= 0.8 ? '#10B981' : '#F59E0B' }}>
                      {((selectedEvolution.confidence || 0) * 100).toFixed(0)}%
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Created:</td>
                  <td style={styles.dataValue}>{formatDate(selectedEvolution.createdAt)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* AI Reasoning */}
          <div style={styles.section}>
            <h3>💭 AI Reasoning</h3>
            <div style={{
              padding: '16px',
              background: '#F9FAFB',
              borderRadius: '8px',
              fontSize: '15px',
              lineHeight: '1.6'
            }}>
              {selectedEvolution.reasoning || 'No reasoning provided'}
            </div>
          </div>

          {/* Proposed Changes */}
          <div style={styles.section}>
            <h3>📝 Proposed Changes</h3>
            <pre style={{
              padding: '16px',
              background: '#1F2937',
              color: '#F9FAFB',
              borderRadius: '8px',
              fontSize: '13px',
              overflow: 'auto',
              maxHeight: '300px'
            }}>
              {JSON.stringify(selectedEvolution.suggestion, null, 2)}
            </pre>
          </div>

          {/* Expected Impact */}
          {selectedEvolution.expectedImpact && (
            <div style={styles.section}>
              <h3>📊 Expected Impact</h3>
              <div style={{
                padding: '16px',
                background: '#DBEAFE',
                borderRadius: '8px',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>
                {selectedEvolution.expectedImpact}
              </div>
            </div>
          )}

          {/* Rejection Reason */}
          <div style={styles.section}>
            <h3>Rejection Reason (if rejecting)</h3>
            <select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              style={styles.select}
            >
              <option value="">Select reason...</option>
              <option>Suggestion not aligned with business goals</option>
              <option>Risk too high for deployment</option>
              <option>Already implemented manually</option>
              <option>Needs more testing</option>
              <option>Conflicts with existing rules</option>
              <option>AI confidence too low</option>
              <option>Not applicable to current system state</option>
            </select>
          </div>

          {/* Admin Notes */}
          <div style={styles.section}>
            <h3>Admin Notes (Optional)</h3>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this AI suggestion..."
              style={styles.textarea}
            />
          </div>

          {/* Actions */}
          <div style={styles.modalActions}>
            <button
              onClick={handleReject}
              disabled={processing}
              style={styles.rejectButton}
            >
              ❌ Reject
            </button>
            <button
              onClick={handleApprove}
              disabled={processing}
              style={styles.approveButton}
            >
              ✅ Approve & Deploy
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main AI Dashboard view
  return (
    <div>
      {/* AI Performance Metrics */}
      <h2>🤖 AI Dashboard</h2>
      <p style={{ color: '#6B7280', marginBottom: '24px' }}>
        Monitor AI decisions and approve suggested improvements
      </p>

      {metrics && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total AI Calls (7d)</div>
            <div style={styles.statValue}>{metrics.totalCalls}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Avg Response Time</div>
            <div style={styles.statValue}>{metrics.avgResponseTime}s</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Total Cost (7d)</div>
            <div style={styles.statValue}>${metrics.totalCost}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Accuracy Rate</div>
            <div style={{ ...styles.statValue, color: parseFloat(metrics.accuracyRate) >= 90 ? '#10B981' : '#F59E0B' }}>
              {metrics.accuracyRate}%
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Pending Approvals</div>
            <div style={{ ...styles.statValue, color: metrics.pendingApprovals > 0 ? '#EF4444' : '#10B981' }}>
              {metrics.pendingApprovals}
            </div>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ ...styles.tabs, marginTop: '32px', borderTop: '1px solid #E5E7EB', paddingTop: '0' }}>
        <button
          onClick={() => setActiveSubTab('pending')}
          style={activeSubTab === 'pending' ? styles.tabActive : styles.tab}
        >
          🚨 Pending ({pendingEvolutions.length})
        </button>
        <button
          onClick={() => setActiveSubTab('decisions')}
          style={activeSubTab === 'decisions' ? styles.tabActive : styles.tab}
        >
          📊 AI Decisions ({decisions.length})
        </button>
        <button
          onClick={() => setActiveSubTab('all')}
          style={activeSubTab === 'all' ? styles.tabActive : styles.tab}
        >
          📜 All Suggestions ({evolutions.length})
        </button>
      </div>

      {/* Pending Approvals */}
      {activeSubTab === 'pending' && (
        <div style={{ marginTop: '24px' }}>
          {pendingEvolutions.length === 0 ? (
            <div style={styles.emptyState}>
              <h3>✅ All Caught Up!</h3>
              <p>No pending AI suggestions at the moment.</p>
            </div>
          ) : (
            pendingEvolutions.map(evolution => (
              <div key={evolution.id} style={styles.jobCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <h3>🤖 {evolution.ruleType || 'Unknown Type'}</h3>
                    <p style={styles.cardSubtitle}>
                      Confidence: {((evolution.confidence || 0) * 100).toFixed(0)}% •
                      Created: {formatDate(evolution.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedEvolution(evolution)}
                    style={styles.reviewButton}
                  >
                    Review →
                  </button>
                </div>
                <p style={{ fontSize: '14px', color: '#6B7280' }}>
                  {evolution.reasoning ? evolution.reasoning.substring(0, 150) + '...' : 'No description'}
                </p>
              </div>
            ))
          )}
        </div>
      )}

      {/* AI Decisions Log */}
      {activeSubTab === 'decisions' && (
        <div style={{ marginTop: '24px' }}>
          {decisions.length === 0 ? (
            <div style={styles.emptyState}>
              <h3>No AI Decisions Found</h3>
              <p>AI decisions will appear here once they are made.</p>
            </div>
          ) : (
            decisions.map(decision => (
              <div key={decision.id} style={styles.jobCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <h4>{decision.type || decision.decisionType || decision.action || 'AI Decision'}</h4>
                    <p style={styles.cardSubtitle}>
                      {formatDate(decision.createdAt || decision.timestamp)} • ID: {decision.id.substring(0, 8)}...
                    </p>
                  </div>
                  {(decision.confidence || decision.score) && (
                    <div style={{
                      padding: '6px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      background: (decision.confidence || decision.score) >= 0.8 ? '#D1FAE5' : '#FEF3C7',
                      color: (decision.confidence || decision.score) >= 0.8 ? '#065F46' : '#92400E'
                    }}>
                      {((decision.confidence || decision.score) * 100).toFixed(0)}% confidence
                    </div>
                  )}
                </div>
                {(decision.reasoning || decision.description || decision.details) && (
                  <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
                    {decision.reasoning || decision.description || decision.details}
                  </p>
                )}
                {decision.cost && (
                  <p style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '8px' }}>
                    Cost: ${decision.cost.toFixed(4)} • Response Time: {decision.responseTime || 'N/A'}s
                  </p>
                )}
                {/* Debug: Show all fields */}
                <details style={{ marginTop: '12px', cursor: 'pointer' }}>
                  <summary style={{ color: '#6B7280', fontSize: '13px' }}>View Raw Data</summary>
                  <pre style={{
                    marginTop: '8px',
                    padding: '12px',
                    background: '#F3F4F6',
                    borderRadius: '6px',
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(decision, null, 2)}
                  </pre>
                </details>
              </div>
            ))
          )}
        </div>
      )}

      {/* All Suggestions (including approved and rejected) */}
      {activeSubTab === 'all' && (
        <div style={{ marginTop: '24px' }}>
          {evolutions.length === 0 ? (
            <div style={styles.emptyState}>
              <h3>No AI Suggestions Found</h3>
              <p>AI suggestions will appear here once they are generated.</p>
            </div>
          ) : (
            <>
              {/* Approved */}
              {approvedEvolutions.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  <h3>✅ Approved ({approvedEvolutions.length})</h3>
                  {approvedEvolutions.map(evolution => (
                    <div key={evolution.id} style={styles.jobCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <h4>{evolution.ruleType || 'Unknown Type'}</h4>
                          <p style={styles.cardSubtitle}>
                            Approved: {formatDate(evolution.approvedAt)} •
                            Confidence: {((evolution.confidence || 0) * 100).toFixed(0)}%
                          </p>
                        </div>
                        <div style={{
                          padding: '6px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '600',
                          background: '#D1FAE5',
                          color: '#065F46'
                        }}>
                          ✅ Approved
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pending */}
              {pendingEvolutions.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  <h3>⏳ Pending Review ({pendingEvolutions.length})</h3>
                  {pendingEvolutions.map(evolution => (
                    <div key={evolution.id} style={styles.jobCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <h4>{evolution.ruleType || 'Unknown Type'}</h4>
                          <p style={styles.cardSubtitle}>
                            Created: {formatDate(evolution.createdAt)} •
                            Confidence: {((evolution.confidence || 0) * 100).toFixed(0)}%
                          </p>
                        </div>
                        <button
                          onClick={() => setSelectedEvolution(evolution)}
                          style={styles.reviewButton}
                        >
                          Review →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Rejected */}
              {rejectedEvolutions.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  <h3>❌ Rejected ({rejectedEvolutions.length})</h3>
                  {rejectedEvolutions.map(evolution => (
                    <div key={evolution.id} style={styles.jobCard}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <h4>{evolution.ruleType || 'Unknown Type'}</h4>
                          <p style={styles.cardSubtitle}>
                            Rejected: {formatDate(evolution.rejectedAt)} •
                            Reason: {evolution.rejectionReason || 'No reason provided'}
                          </p>
                        </div>
                        <div style={{
                          padding: '6px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '600',
                          background: '#FEE2E2',
                          color: '#991B1B'
                        }}>
                          ❌ Rejected
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Community Tab Component
function CommunityTab({ posts, onReply, onResolve, onUpdateStatus, onAIAnalyze }) {
  const [selectedPost, setSelectedPost] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-MY', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter posts
  const filteredPosts = posts.filter(post => {
    if (filterCategory !== 'all' && post.category !== filterCategory) return false;
    if (filterStatus !== 'all' && post.status !== filterStatus) return false;
    return true;
  });

  // Group by status
  const pendingPosts = filteredPosts.filter(p => p.status === 'pending');
  const repliedPosts = filteredPosts.filter(p => p.status === 'replied');
  const resolvedPosts = filteredPosts.filter(p => p.status === 'resolved');

  const handleReply = async () => {
    if (!replyText.trim()) {
      alert('Please enter a reply');
      return;
    }

    setProcessing(true);
    try {
      await onReply(selectedPost.id, replyText);
      alert('✅ Reply sent successfully!');
      setSelectedPost(null);
      setReplyText('');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleResolve = async () => {
    if (!window.confirm('Mark this post as resolved?')) return;

    setProcessing(true);
    try {
      await onResolve(selectedPost.id, resolutionNotes);
      alert('✅ Post marked as resolved!');
      setSelectedPost(null);
      setResolutionNotes('');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Detail Modal
  if (selectedPost) {
    const getCategoryColor = (category) => {
      const colors = {
        bug: { bg: '#FEE2E2', color: '#991B1B', icon: '🐛' },
        feature: { bg: '#DBEAFE', color: '#1E40AF', icon: '💡' },
        feedback: { bg: '#D1FAE5', color: '#065F46', icon: '💬' },
        question: { bg: '#FEF3C7', color: '#92400E', icon: '❓' },
        complaint: { bg: '#FED7AA', color: '#9A3412', icon: '⚠️' }
      };
      return colors[category] || { bg: '#F3F4F6', color: '#6B7280', icon: '📝' };
    };

    const categoryStyle = getCategoryColor(selectedPost.category);

    return (
      <div style={styles.reviewModal}>
        <div style={styles.modalHeader}>
          <h2>Community Post Details</h2>
          <button onClick={() => setSelectedPost(null)} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.modalContent}>
          {/* Post Info */}
          <div style={styles.section}>
            <h3>Post Information</h3>
            <table style={styles.dataTable}>
              <tbody>
                <tr>
                  <td style={styles.dataLabel}>User Type:</td>
                  <td style={styles.dataValue}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      background: selectedPost.userType === 'business' ? '#DBEAFE' : '#D1FAE5',
                      color: selectedPost.userType === 'business' ? '#1E40AF' : '#065F46'
                    }}>
                      {selectedPost.userType === 'business' ? '🏢 Business' : '👷 Worker'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>User Name:</td>
                  <td style={styles.dataValue}>{selectedPost.userName || 'Anonymous'}</td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Category:</td>
                  <td style={styles.dataValue}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      background: categoryStyle.bg,
                      color: categoryStyle.color
                    }}>
                      {categoryStyle.icon} {selectedPost.category || 'General'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td style={styles.dataLabel}>Posted:</td>
                  <td style={styles.dataValue}>{formatDate(selectedPost.createdAt)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Post Content */}
          <div style={styles.section}>
            <h3>Title</h3>
            <div style={{
              padding: '12px 16px',
              background: '#F9FAFB',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              {selectedPost.title || 'No title'}
            </div>
          </div>

          <div style={styles.section}>
            <h3>Description</h3>
            <div style={{
              padding: '16px',
              background: '#F9FAFB',
              borderRadius: '8px',
              fontSize: '15px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap'
            }}>
              {selectedPost.content || 'No content'}
            </div>
          </div>

          {/* AI Moderation Status */}
          {selectedPost.flagged && (
            <div style={styles.section}>
              <h3>⚠️ AI Moderation Alert</h3>
              <div style={{
                padding: '16px',
                background: '#FEE2E2',
                borderRadius: '8px',
                borderLeft: '4px solid #EF4444'
              }}>
                <strong style={{ color: '#991B1B' }}>Reason:</strong> {selectedPost.flagReason}
                {selectedPost.moderatedContent && (
                  <>
                    <br /><br />
                    <strong style={{ color: '#991B1B' }}>Moderated Content:</strong>
                    <p style={{ marginTop: '8px' }}>{selectedPost.moderatedContent}</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* AI Analysis */}
          {selectedPost.aiAnalysis && (
            <div style={styles.section}>
              <h3>🤖 AI Analysis & Suggestions</h3>
              <div style={{
                padding: '16px',
                background: '#DBEAFE',
                borderRadius: '8px',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>
                {selectedPost.aiAnalysis}
              </div>
            </div>
          )}

          {/* Admin Reply */}
          {selectedPost.adminReply && (
            <div style={styles.section}>
              <h3>✅ Admin Reply</h3>
              <div style={{
                padding: '16px',
                background: '#D1FAE5',
                borderRadius: '8px',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>
                {selectedPost.adminReply}
                <div style={{ fontSize: '13px', color: '#065F46', marginTop: '8px' }}>
                  Replied: {formatDate(selectedPost.repliedAt)}
                </div>
              </div>
            </div>
          )}

          {/* Reply Section */}
          {selectedPost.status !== 'resolved' && (
            <>
              <div style={styles.section}>
                <h3>Admin Reply</h3>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write your reply to the user..."
                  style={styles.textarea}
                />
              </div>

              <div style={styles.section}>
                <h3>Resolution Notes (Optional)</h3>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Internal notes about resolution..."
                  style={styles.textarea}
                />
              </div>

              <div style={styles.modalActions}>
                <button
                  onClick={handleReply}
                  disabled={processing || !replyText.trim()}
                  style={{
                    ...styles.approveButton,
                    opacity: (!replyText.trim() || processing) ? 0.5 : 1
                  }}
                >
                  {processing ? 'Sending...' : '💬 Send Reply'}
                </button>
                <button
                  onClick={handleResolve}
                  disabled={processing}
                  style={{
                    ...styles.approveButton,
                    background: '#10B981'
                  }}
                >
                  {processing ? 'Processing...' : '✅ Mark Resolved'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2>💬 Community Forum</h2>
      <p style={{ color: '#6B7280', marginBottom: '24px' }}>
        User feedback, bug reports, feature requests, and general discussions
      </p>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', display: 'block' }}>
            Category
          </label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Categories</option>
            <option value="bug">🐛 Bug Reports</option>
            <option value="feature">💡 Feature Requests</option>
            <option value="feedback">💬 Feedback</option>
            <option value="question">❓ Questions</option>
            <option value="complaint">⚠️ Complaints</option>
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '14px', fontWeight: '600', color: '#6B7280', marginBottom: '8px', display: 'block' }}>
            Status
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Statuses</option>
            <option value="pending">⏳ Pending</option>
            <option value="replied">💬 Replied</option>
            <option value="resolved">✅ Resolved</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Pending</div>
          <div style={{ ...styles.statValue, color: '#F59E0B' }}>{pendingPosts.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Replied</div>
          <div style={{ ...styles.statValue, color: '#3B82F6' }}>{repliedPosts.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Resolved</div>
          <div style={{ ...styles.statValue, color: '#10B981' }}>{resolvedPosts.length}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Posts</div>
          <div style={styles.statValue}>{posts.length}</div>
        </div>
      </div>

      {/* Posts List */}
      <div style={{ marginTop: '24px' }}>
        {filteredPosts.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>No Posts Found</h3>
            <p>No community posts matching the selected filters.</p>
          </div>
        ) : (
          filteredPosts.map(post => {
            const getCategoryColor = (category) => {
              const colors = {
                bug: { bg: '#FEE2E2', color: '#991B1B', icon: '🐛' },
                feature: { bg: '#DBEAFE', color: '#1E40AF', icon: '💡' },
                feedback: { bg: '#D1FAE5', color: '#065F46', icon: '💬' },
                question: { bg: '#FEF3C7', color: '#92400E', icon: '❓' },
                complaint: { bg: '#FED7AA', color: '#9A3412', icon: '⚠️' }
              };
              return colors[category] || { bg: '#F3F4F6', color: '#6B7280', icon: '📝' };
            };

            const categoryStyle = getCategoryColor(post.category);

            return (
              <div
                key={post.id}
                style={{
                  ...styles.jobCard,
                  borderLeft: post.flagged ? '4px solid #EF4444' : '4px solid #E5E7EB',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedPost(post)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <h3 style={{ margin: 0 }}>{post.title || 'No title'}</h3>
                      {post.flagged && (
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: '#FEE2E2',
                          color: '#991B1B'
                        }}>
                          ⚠️ FLAGGED
                        </span>
                      )}
                    </div>
                    <p style={styles.cardSubtitle}>
                      {post.userType === 'business' ? '🏢' : '👷'} {post.userName || 'Anonymous'} •
                      {formatDate(post.createdAt)} •
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        marginLeft: '8px',
                        background: categoryStyle.bg,
                        color: categoryStyle.color,
                        fontWeight: '600'
                      }}>
                        {categoryStyle.icon} {post.category || 'General'}
                      </span>
                    </p>
                    <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px', marginBottom: 0 }}>
                      {post.content && post.content.length > 150
                        ? post.content.substring(0, 150) + '...'
                        : post.content || 'No content'}
                    </p>
                  </div>
                  <div style={{
                    padding: '8px 16px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '600',
                    height: 'fit-content',
                    background: post.status === 'resolved' ? '#D1FAE5' :
                               post.status === 'replied' ? '#DBEAFE' : '#FEF3C7',
                    color: post.status === 'resolved' ? '#065F46' :
                           post.status === 'replied' ? '#1E40AF' : '#92400E'
                  }}>
                    {post.status === 'resolved' ? '✅ Resolved' :
                     post.status === 'replied' ? '💬 Replied' : '⏳ Pending'}
                  </div>
                </div>

                {post.aiAnalysis && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    background: '#EFF6FF',
                    borderRadius: '6px',
                    borderLeft: '3px solid #3B82F6',
                    fontSize: '13px',
                    color: '#1E40AF'
                  }}>
                    <strong>🤖 AI:</strong> {post.aiAnalysis.substring(0, 100)}...
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Messages Tab Component
function MessagesTab({ users, messages, broadcasts, currentUserId, onSendMessage, onSendBroadcast, onRefresh }) {
  const [activeSubTab, setActiveSubTab] = useState('send');
  const [recipientId, setRecipientId] = useState('');
  const [messageTitle, setMessageTitle] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [priority, setPriority] = useState('default');
  const [category, setCategory] = useState('support');
  const [targetAudience, setTargetAudience] = useState('all');
  const [userSearch, setUserSearch] = useState('');

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.displayName?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleSendMessage = async () => {
    if (!recipientId || !messageTitle || !messageContent) {
      alert('Please fill in all fields');
      return;
    }
    try {
      await onSendMessage(recipientId, messageTitle, messageContent, priority, category);
      setRecipientId('');
      setMessageTitle('');
      setMessageContent('');
      alert('Message sent successfully!');
    } catch (error) {
      alert('Error sending message: ' + error.message);
    }
  };

  const handleSendBroadcast = async () => {
    if (!messageTitle || !messageContent) {
      alert('Please fill in all fields');
      return;
    }
    try {
      await onSendBroadcast(messageTitle, messageContent, priority, category, targetAudience);
      setMessageTitle('');
      setMessageContent('');
      alert('Broadcast sent successfully!');
    } catch (error) {
      alert('Error sending broadcast: ' + error.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveSubTab('send')}
          style={{
            padding: '10px 20px',
            background: activeSubTab === 'send' ? '#3B82F6' : '#E5E7EB',
            color: activeSubTab === 'send' ? 'white' : 'black',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Send Message
        </button>
        <button
          onClick={() => setActiveSubTab('broadcast')}
          style={{
            padding: '10px 20px',
            background: activeSubTab === 'broadcast' ? '#3B82F6' : '#E5E7EB',
            color: activeSubTab === 'broadcast' ? 'white' : 'black',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Send Broadcast
        </button>
        <button
          onClick={() => setActiveSubTab('history')}
          style={{
            padding: '10px 20px',
            background: activeSubTab === 'history' ? '#3B82F6' : '#E5E7EB',
            color: activeSubTab === 'history' ? 'white' : 'black',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Message History ({messages.length})
        </button>
        <button
          onClick={() => setActiveSubTab('broadcasts-list')}
          style={{
            padding: '10px 20px',
            background: activeSubTab === 'broadcasts-list' ? '#3B82F6' : '#E5E7EB',
            color: activeSubTab === 'broadcasts-list' ? 'white' : 'black',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Broadcasts ({broadcasts.length})
        </button>
        <button
          onClick={onRefresh}
          style={{
            padding: '10px 20px',
            background: '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            marginLeft: 'auto'
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {activeSubTab === 'send' && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0 }}>📨 Send Message to User</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Search User</label>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search by email or name..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #D1D5DB', borderRadius: '4px' }}>
            {filteredUsers.slice(0, 10).map(u => (
              <div
                key={u.id}
                onClick={() => setRecipientId(u.id)}
                style={{
                  padding: '10px',
                  cursor: 'pointer',
                  background: recipientId === u.id ? '#EFF6FF' : 'white',
                  borderBottom: '1px solid #E5E7EB'
                }}
              >
                <div style={{ fontWeight: 'bold' }}>{u.displayName || 'Unknown'}</div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>{u.email} | {u.role}</div>
              </div>
            ))}
          </div>

          {recipientId && (
            <div style={{ marginBottom: '15px', padding: '10px', background: '#ECFDF5', borderRadius: '4px' }}>
              ✓ Selected: {users.find(u => u.id === recipientId)?.email}
            </div>
          )}

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Title</label>
            <input
              type="text"
              value={messageTitle}
              onChange={(e) => setMessageTitle(e.target.value)}
              placeholder="Message title..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Message</label>
            <textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Your message..."
              rows="5"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              >
                <option value="low">Low</option>
                <option value="default">Default</option>
                <option value="high">High</option>
              </select>
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              >
                <option value="support">Support</option>
                <option value="verification">Verification</option>
                <option value="payment">Payment</option>
                <option value="announcement">Announcement</option>
                <option value="test">Test</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleSendMessage}
            style={{
              padding: '12px 24px',
              background: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            📨 Send Message
          </button>
        </div>
      )}

      {activeSubTab === 'broadcast' && (
        <div style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0 }}>📢 Send Broadcast Message</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Title</label>
            <input
              type="text"
              value={messageTitle}
              onChange={(e) => setMessageTitle(e.target.value)}
              placeholder="Broadcast title..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Message</label>
            <textarea
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              placeholder="Your broadcast message..."
              rows="5"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D1D5DB',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Priority</label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              >
                <option value="low">Low</option>
                <option value="default">Default</option>
                <option value="high">High</option>
              </select>
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              >
                <option value="support">Support</option>
                <option value="verification">Verification</option>
                <option value="payment">Payment</option>
                <option value="announcement">Announcement</option>
                <option value="test">Test</option>
              </select>
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Target Audience</label>
              <select
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px'
                }}
              >
                <option value="all">All Users</option>
                <option value="workers">Workers Only</option>
                <option value="businesses">Businesses Only</option>
                <option value="verified_only">Verified Users Only</option>
              </select>
            </div>
          </div>

          <button
            onClick={handleSendBroadcast}
            style={{
              padding: '12px 24px',
              background: '#3B82F6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold'
            }}
          >
            📢 Send Broadcast
          </button>
        </div>
      )}

      {activeSubTab === 'history' && (
        <div>
          <h3>📬 Message History ({messages.length})</h3>
          {messages.length === 0 ? (
            <p>No messages sent yet.</p>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  background: 'white',
                  padding: '15px',
                  marginBottom: '10px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  borderLeft: `4px solid ${msg.priority === 'high' ? '#EF4444' : msg.priority === 'low' ? '#10B981' : '#3B82F6'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <strong>{msg.title}</strong>
                    <span style={{
                      marginLeft: '10px',
                      padding: '2px 8px',
                      background: '#E5E7EB',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {msg.category}
                    </span>
                    <span style={{
                      marginLeft: '5px',
                      padding: '2px 8px',
                      background: msg.priority === 'high' ? '#FEE2E2' : msg.priority === 'low' ? '#D1FAE5' : '#DBEAFE',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {msg.priority}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    {msg.createdAt?.toDate?.().toLocaleString() || 'Unknown date'}
                  </div>
                </div>
                <div style={{ fontSize: '14px', color: '#374151', marginBottom: '8px' }}>
                  {msg.message}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  To: {users.find(u => u.id === msg.recipientId)?.email || msg.recipientId} |
                  From: {msg.senderName} |
                  Read: {msg.read ? '✓' : '✗'}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeSubTab === 'broadcasts-list' && (
        <div>
          <h3>📢 Broadcast Messages ({broadcasts.length})</h3>
          {broadcasts.length === 0 ? (
            <p>No broadcasts sent yet.</p>
          ) : (
            broadcasts.map((bc) => (
              <div
                key={bc.id}
                style={{
                  background: 'white',
                  padding: '15px',
                  marginBottom: '10px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  borderLeft: `4px solid ${bc.priority === 'high' ? '#EF4444' : bc.priority === 'low' ? '#10B981' : '#3B82F6'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div>
                    <strong>{bc.title}</strong>
                    <span style={{
                      marginLeft: '10px',
                      padding: '2px 8px',
                      background: '#E5E7EB',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {bc.category}
                    </span>
                    <span style={{
                      marginLeft: '5px',
                      padding: '2px 8px',
                      background: bc.active ? '#D1FAE5' : '#FEE2E2',
                      borderRadius: '12px',
                      fontSize: '12px'
                    }}>
                      {bc.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>
                    {bc.createdAt?.toDate?.().toLocaleString() || 'Unknown date'}
                  </div>
                </div>
                <div style={{ fontSize: '14px', color: '#374151', marginBottom: '8px' }}>
                  {bc.message}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  Target: {bc.targetAudience} | Priority: {bc.priority}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ===== SETTINGS TAB - 2FA ENROLLMENT =====
function SettingsTab({
  user,
  twoFAStatus,
  setTwoFAStatus,
  qrCode,
  setQRCode,
  twoFASecret,
  setTwoFASecret,
  verificationCode,
  setVerificationCode,
  backupCodes,
  setBackupCodes
}) {
  const [enrollmentStep, setEnrollmentStep] = useState('idle'); // idle | qr_code | verifying | complete
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // Load 2FA status on mount
  useEffect(() => {
    loadTwoFAStatus();
  }, [user]);

  const loadTwoFAStatus = async () => {
    if (!user) return;
    try {
      const status = await check2FAStatus(user.uid);
      setTwoFAStatus(status);
    } catch (err) {
      console.error('Error loading 2FA status:', err);
    }
  };

  const handleEnroll2FA = async () => {
    setLoading(true);
    setError('');
    try {
      // Generate TOTP secret and QR code
      const { secret, qrCode: qrCodeDataUrl } = await setup2FA(user.email);
      setTwoFASecret(secret);
      setQRCode(qrCodeDataUrl);
      setEnrollmentStep('qr_code');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Verify the code first
      const isValid = verify2FACode(verificationCode, twoFASecret);
      if (!isValid) {
        throw new Error('Invalid code. Please try again.');
      }

      // Generate backup codes
      const codes = generateBackupCodes();
      setBackupCodes(codes);

      // Enable 2FA in Firestore
      await enable2FA(user.uid, twoFASecret, codes);

      setEnrollmentStep('complete');
      setShowBackupCodes(true);
      await loadTwoFAStatus();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await disable2FA(user.uid);
      setTwoFAStatus(null);
      setEnrollmentStep('idle');
      setQRCode(null);
      setTwoFASecret(null);
      setVerificationCode('');
      setBackupCodes([]);
      setShowBackupCodes(false);
      alert('2FA has been disabled.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ marginBottom: '24px' }}>⚙️ Settings</h2>

      {/* 2FA Section */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '24px'
      }}>
        <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          🔐 Two-Factor Authentication (2FA)
          {twoFAStatus?.enabled && (
            <span style={{
              fontSize: '12px',
              background: '#10B981',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontWeight: 'normal'
            }}>
              ENABLED
            </span>
          )}
        </h3>
        <p style={{ color: '#6B7280', marginBottom: '20px' }}>
          Add an extra layer of security to your admin account using Google Authenticator.
        </p>

        {error && (
          <div style={{
            padding: '12px',
            background: '#FEE2E2',
            border: '1px solid #FCA5A5',
            borderRadius: '8px',
            color: '#DC2626',
            marginBottom: '16px'
          }}>
            {error}
          </div>
        )}

        {/* Not Enrolled - Show Enroll Button */}
        {!twoFAStatus?.enabled && enrollmentStep === 'idle' && (
          <div>
            <button
              onClick={handleEnroll2FA}
              disabled={loading}
              style={{
                padding: '12px 24px',
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Setting up...' : 'Enable 2FA'}
            </button>
          </div>
        )}

        {/* QR Code Step */}
        {enrollmentStep === 'qr_code' && qrCode && (
          <div>
            <div style={{
              background: '#F3F4F6',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <h4 style={{ marginBottom: '12px' }}>Step 1: Scan QR Code</h4>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px' }}>
                Open Google Authenticator (or any TOTP app) and scan this QR code:
              </p>
              <div style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                display: 'inline-block',
                marginBottom: '16px'
              }}>
                <img src={qrCode} alt="2FA QR Code" style={{ display: 'block' }} />
              </div>
              <p style={{ fontSize: '12px', color: '#9CA3AF' }}>
                Can't scan? Manual entry: <code style={{ background: '#E5E7EB', padding: '2px 6px', borderRadius: '4px' }}>{twoFASecret}</code>
              </p>
            </div>

            <div style={{
              background: '#F3F4F6',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <h4 style={{ marginBottom: '12px' }}>Step 2: Enter Verification Code</h4>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '12px' }}>
                Enter the 6-digit code from your authenticator app:
              </p>
              <input
                type="text"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength="6"
                style={{
                  width: '150px',
                  padding: '12px',
                  fontSize: '24px',
                  textAlign: 'center',
                  border: '2px solid #D1D5DB',
                  borderRadius: '8px',
                  letterSpacing: '8px',
                  fontFamily: 'monospace',
                  marginBottom: '12px'
                }}
              />
              <br />
              <button
                onClick={handleVerifyAndEnable}
                disabled={loading || verificationCode.length !== 6}
                style={{
                  padding: '12px 24px',
                  background: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (loading || verificationCode.length !== 6) ? 'not-allowed' : 'pointer',
                  opacity: (loading || verificationCode.length !== 6) ? 0.6 : 1,
                  marginRight: '12px'
                }}
              >
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>
              <button
                onClick={() => {
                  setEnrollmentStep('idle');
                  setQRCode(null);
                  setTwoFASecret(null);
                  setVerificationCode('');
                }}
                disabled={loading}
                style={{
                  padding: '12px 24px',
                  background: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Enrollment Complete - Show Backup Codes */}
        {enrollmentStep === 'complete' && showBackupCodes && backupCodes.length > 0 && (
          <div style={{
            background: '#FEF3C7',
            border: '2px solid #F59E0B',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '16px'
          }}>
            <h4 style={{ color: '#92400E', marginBottom: '12px' }}>⚠️ IMPORTANT: Save Your Backup Codes</h4>
            <p style={{ color: '#78350F', marginBottom: '12px', fontSize: '14px' }}>
              These codes can be used if you lose access to your authenticator app. Each code can only be used once. Save them in a secure location!
            </p>
            <div style={{
              background: 'white',
              padding: '16px',
              borderRadius: '8px',
              fontFamily: 'monospace',
              fontSize: '14px',
              marginBottom: '12px',
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '8px'
            }}>
              {backupCodes.map((code, index) => (
                <div key={index} style={{ padding: '8px', background: '#F3F4F6', borderRadius: '4px' }}>
                  {index + 1}. {code}
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                const codesText = backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n');
                navigator.clipboard.writeText(codesText);
                alert('Backup codes copied to clipboard!');
              }}
              style={{
                padding: '8px 16px',
                background: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                marginRight: '8px'
              }}
            >
              📋 Copy All Codes
            </button>
            <button
              onClick={() => {
                setShowBackupCodes(false);
                setEnrollmentStep('idle');
              }}
              style={{
                padding: '8px 16px',
                background: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              ✅ I've Saved My Codes
            </button>
          </div>
        )}

        {/* Already Enrolled - Show Status and Disable Option */}
        {twoFAStatus?.enabled && enrollmentStep === 'idle' && (
          <div>
            <div style={{
              background: '#D1FAE5',
              border: '1px solid #A7F3D0',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <p style={{ color: '#065F46', marginBottom: '4px', fontWeight: '600' }}>
                ✅ Two-Factor Authentication is Active
              </p>
              <p style={{ color: '#047857', fontSize: '14px' }}>
                Your account is protected with TOTP 2FA. You'll be asked for a code when logging in.
              </p>
            </div>
            <button
              onClick={handleDisable2FA}
              disabled={loading}
              style={{
                padding: '10px 20px',
                background: '#EF4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'Disabling...' : 'Disable 2FA'}
            </button>
          </div>
        )}
      </div>

      {/* Account Info */}
      <div style={{
        background: 'white',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginBottom: '16px' }}>👤 Account Information</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <span style={{ color: '#6B7280', fontSize: '14px' }}>Email:</span>
            <p style={{ margin: '4px 0 0 0', fontWeight: '600' }}>{user.email}</p>
          </div>
          <div>
            <span style={{ color: '#6B7280', fontSize: '14px' }}>User ID:</span>
            <p style={{ margin: '4px 0 0 0', fontFamily: 'monospace', fontSize: '12px' }}>{user.uid}</p>
          </div>
          <div>
            <span style={{ color: '#6B7280', fontSize: '14px' }}>Email Verified:</span>
            <p style={{ margin: '4px 0 0 0' }}>
              {user.emailVerified ? (
                <span style={{ color: '#10B981', fontWeight: '600' }}>✅ Verified</span>
              ) : (
                <span style={{ color: '#EF4444', fontWeight: '600' }}>❌ Not Verified</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Styles
const styles = {
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '20px'
  },
  loginContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  },
  loginBox: {
    background: 'white',
    padding: '40px',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    width: '100%',
    maxWidth: '400px'
  },
  loginTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    marginBottom: '8px',
    textAlign: 'center',
    color: '#1F2937'
  },
  loginSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: '32px'
  },
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  input: {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    fontSize: '16px',
    outline: 'none'
  },
  loginButton: {
    padding: '14px',
    background: 'linear-gradient(135deg, #FD7F3A 0%, #FF6B35 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  error: {
    color: '#EF4444',
    fontSize: '14px',
    textAlign: 'center'
  },
  dashboard: {
    minHeight: '100vh',
    background: '#F3F4F6'
  },
  header: {
    background: 'white',
    padding: '20px 32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '16px'
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1F2937'
  },
  headerSubtitle: {
    fontSize: '14px',
    color: '#6B7280'
  },
  logoutButton: {
    padding: '8px 16px',
    background: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  mainLayout: {
    display: 'flex',
    flexDirection: 'row',
    minHeight: 'calc(100vh - 72px)'
  },
  sidebar: {
    width: '250px',
    background: 'white',
    borderRight: '1px solid #E5E7EB',
    padding: '16px 0',
    overflowY: 'auto',
    flexShrink: 0
  },
  tab: {
    width: '100%',
    padding: '12px 20px',
    background: 'none',
    border: 'none',
    borderLeft: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6B7280',
    textAlign: 'left',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  tabActive: {
    width: '100%',
    padding: '12px 20px',
    background: '#FEF7F0',
    border: 'none',
    borderLeft: '3px solid #FD7F3A',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    color: '#FD7F3A',
    textAlign: 'left',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  content: {
    flex: 1,
    padding: '32px',
    overflowY: 'auto',
    background: '#F3F4F6'
  },
  verificationList: {},
  verificationCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '16px',
    cursor: 'pointer',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'all 0.2s'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  cardSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    marginTop: '4px'
  },
  reviewButton: {
    padding: '8px 16px',
    background: '#FD7F3A',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  cardScores: {
    display: 'flex',
    gap: '8px'
  },
  badge: {
    padding: '4px 12px',
    background: '#F3F4F6',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '500'
  },
  reviewModal: {
    background: 'white',
    borderRadius: '12px',
    maxWidth: '900px',
    margin: '0 auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #E5E7EB'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#6B7280'
  },
  modalContent: {
    padding: '24px',
    maxHeight: '70vh',
    overflowY: 'auto'
  },
  section: {
    marginBottom: '24px'
  },
  idImage: {
    width: '100%',
    maxHeight: '300px',
    objectFit: 'contain',
    borderRadius: '8px',
    border: '1px solid #E5E7EB'
  },
  noImage: {
    padding: '60px',
    background: '#F3F4F6',
    borderRadius: '8px',
    textAlign: 'center',
    color: '#6B7280'
  },
  dataTable: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  dataLabel: {
    padding: '12px 16px',
    borderBottom: '1px solid #E5E7EB',
    fontWeight: '500',
    color: '#6B7280',
    width: '200px'
  },
  dataValue: {
    padding: '12px 16px',
    borderBottom: '1px solid #E5E7EB',
    color: '#1F2937'
  },
  scores: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  scoreCard: {
    padding: '20px',
    background: '#F9FAFB',
    borderRadius: '8px',
    textAlign: 'center'
  },
  scoreLabel: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '8px'
  },
  scoreValue: {
    fontSize: '32px',
    fontWeight: 'bold'
  },
  issuesList: {
    margin: 0,
    paddingLeft: '20px'
  },
  issueItem: {
    padding: '8px 0',
    color: '#EF4444'
  },
  select: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    fontSize: '15px'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    fontSize: '15px',
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  rejectButton: {
    flex: 1,
    padding: '14px',
    background: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  approveButton: {
    flex: 1,
    padding: '14px',
    background: '#10B981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  jobsList: {},
  jobCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  commissionContainer: {},
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginTop: '24px'
  },
  statCard: {
    background: 'white',
    padding: '24px',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  statLabel: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '8px'
  },
  statValue: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#10B981'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px',
    background: 'white',
    borderRadius: '12px'
  },
  payoutsList: {},
  payoutsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  exportButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
  },
  totalPending: {
    background: 'white',
    padding: '16px 24px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  payoutCard: {
    background: 'white',
    padding: '24px',
    borderRadius: '12px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '2px solid #FEE2E2'
  },
  payoutCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  },
  payoutCardDetails: {
    display: 'flex',
    gap: '24px',
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '8px',
    marginBottom: '16px'
  },
  payoutDetail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  payoutDetailLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500'
  },
  markPaidButton: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }
};

export default App;
